import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { maintenanceApi, repairApi } from '../services/api';
import { getAuthToken } from '../services/auth';
import { getBaseUrl } from '../services/baseUrl';
import { showInAppAlert, showInAppConfirm } from '../services/inAppDialog';
import { promptCaseCloseAfterServiceReport } from '../utils/serviceReportCaseClose';
import RepairServiceReportModal from './RepairServiceReportModal';
import type { ReferenceImageBlock, ReferenceImageHit } from './TSFADAnalysis.shared';
import type {
  ReportTemplate,
  ServiceReportDocument,
  ServiceReportRecord
} from '../utils/serviceReportTemplate';

type MaintenanceStatus = 'PLANNED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED';
const VALID_MAINTENANCE_STATUSES: MaintenanceStatus[] = ['PLANNED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED'];
type MaintenanceTaskVariant = 'checkup' | 'preventative';
type CheckUpTaskProps = {
  variant?: MaintenanceTaskVariant;
};

type CheckupChecklistItem = {
  id: string;
  label: string;
  spec?: string | null;
  completed: boolean;
  sourceNodeIds?: string[];
  capture?: StepCapture | null;
  captureValue?: string;
  reference?: string | null;
};

type StepCapture = {
  type?: string;
  parameter?: string;
  units?: string;
  target?: number | string | null;
  expectedLow?: number | string | null;
  expectedHigh?: number | string | null;
  tolerance?: number | string | null;
  method?: string;
  tool?: string;
  notes?: string;
};

type DeviceExpertContextOption = {
  key: string;
  stepId: string;
  number: string;
  description: string;
  label: string;
};

type DeviceExpertParentOption = {
  id: string;
  sequence: number;
  label: string;
  checklistItems: CheckupChecklistItem[];
};

type DeviceExpertMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
};

type StepAttachment = {
  id: string;
  name: string;
  size: number;
  dataUrl: string;
  addedAt: string;
};

type PartUsage = {
  id: string;
  partNumber: string;
  description: string;
  batchNumber: string;
  quantity: number | null;
  source?: string | null;
  catalogType?: string | null;
  kitId?: string | null;
  kitNumber?: string | null;
};

type ReferenceKitSummary = {
  id?: string | null;
  kitId?: string | null;
  kitNumber?: string | null;
  sku?: string | null;
  partNumber?: string | null;
  number?: string | null;
  code?: string | null;
  name?: string | null;
  description?: string | null;
  componentCount?: number | null;
  components?: unknown[] | null;
};

type PartDraft = {
  partNumber: string;
  description: string;
  batchNumber: string;
  quantity: string;
};

type StepPartScanState = 'idle' | 'pending' | 'success' | 'error';

type CheckupStep = {
  id: string;
  label: string;
  summary?: string | null;
  checklist: CheckupChecklistItem[];
  partsUsed: PartUsage[];
  collapsed: boolean;
  notes: string;
  attachments: StepAttachment[];
  notApplicable: boolean;
  referenceImages?: ReferenceImageHit[];
  referenceImageBlocks?: ReferenceImageBlock[];
};

type ActiveSlaSummary = {
  active?: boolean;
  label?: string | null;
  brandingName?: string | null;
  tierBrandingName?: string | null;
  slaType?: string | null;
  note?: string | null;
};

type MaintenanceCaseSummary = {
  id: string;
  caseCode?: string | null;
  caseNumber?: string | null;
  problemDescription?: string | null;
  customer?: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  } | null;
  contact?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  device?: {
    id: string;
    modelId?: string | null;
    modelNumber?: string | null;
    modelName?: string | null;
    serialNumber?: string | null;
    manufacturer?: string | null;
    activeSlaSummary?: ActiveSlaSummary | null;
  } | null;
  location?: {
    id?: string | null;
    name?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    stateProvince?: string | null;
    postalCode?: string | null;
    country?: string | null;
  } | null;
};

type MaintenanceWorkspaceStep = {
  id: string;
  label: string;
  summary?: string | null;
  checklist: CheckupChecklistItem[];
  partsUsed?: PartUsage[];
  attachments: StepAttachment[];
  collapsed?: boolean;
  notes?: string | null;
  notApplicable?: boolean;
  status?: string | null;
  referenceImages?: ReferenceImageHit[];
  referenceImageBlocks?: ReferenceImageBlock[];
};

type ProtocolAvailability = {
  available?: boolean;
  isActive?: boolean;
  canContinue?: boolean;
  reason?: string | null;
  message?: string | null;
  referenceType?: string | null;
  modelId?: string | null;
  modelNumber?: string | null;
  modelName?: string | null;
  revisionId?: string | null;
  maintenanceInterval?: string | null;
  hasSavedRun?: boolean;
  activeStepCount?: number;
};

type MaintenanceWorkspacePayload = {
  caseSummary: MaintenanceCaseSummary | null;
  task: {
    id: string;
    status?: string | null;
    updatedAt?: string | null;
  } | null;
  run: {
    id: string;
    notes?: string | null;
    metadata?: Record<string, unknown> | null;
    updatedAt?: string | null;
  } | null;
  steps: MaintenanceWorkspaceStep[];
  reference?: {
    revisionId?: string | null;
    maintenanceInterval?: string | null;
    type?: string | null;
  } | null;
  referenceKit?: ReferenceKitSummary | null;
  protocolAvailability?: ProtocolAvailability | null;
  intervals?: {
    options: Array<{ interval: string; revisionId?: string | null; isDefault?: boolean }>;
    selected: string | null;
  } | null;
  serviceReport?: ServiceReportRecord | null;
  serviceReportTemplate?: ReportTemplate | null;
  access?: {
    canWrite?: boolean;
    assignedUserId?: string | null;
    assignedUserName?: string | null;
  } | null;
};

const MAX_STEP_ATTACHMENTS = 6;
const MAX_ATTACHMENT_BYTES = 6 * 1024 * 1024;
const MISSING_PROTOCOL_ALERT_MESSAGE =
  'No active protocol is available for this task. You can continue manually, or close the task and activate a protocol in Documents.';
const EMPTY_PART_DRAFT: PartDraft = {
  partNumber: '',
  description: '',
  batchNumber: '',
  quantity: ''
};

function AuthenticatedReferenceImage({
  src,
  alt,
  style,
  placeholderStyle,
  placeholderText = 'No preview available'
}: {
  src?: string | null;
  alt: string;
  style: React.CSSProperties;
  placeholderStyle: React.CSSProperties;
  placeholderText?: string;
}) {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) {
      setResolvedSrc(null);
      setFailed(false);
      return undefined;
    }
    if (/^(?:data|blob):/i.test(src)) {
      setResolvedSrc(src);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setResolvedSrc(null);
    setFailed(false);

    const fetchImage = async () => {
      const token = getAuthToken();
      const baseUrl = /^(?:https?:|data:|blob:)/i.test(src)
        ? ''
        : await getBaseUrl().catch(() => '');
      const requestUrl =
        baseUrl && !/^(?:https?:|data:|blob:)/i.test(src)
          ? `${baseUrl.replace(/\/+$/, '')}${src.startsWith('/') ? src : `/${src}`}`
          : src;
      const response = await fetch(requestUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error('Image unavailable');
      return response.blob();
    };

    fetchImage()
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src) return <div style={placeholderStyle}>{placeholderText}</div>;
  if (failed) return <div style={placeholderStyle}>Image unavailable</div>;
  if (!resolvedSrc) return <div style={placeholderStyle}>Loading image...</div>;
  return <img src={resolvedSrc} alt={alt} style={style} />;
}

const normalizeWhitespace = (value: string) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const composeChecklistItemText = (item: CheckupChecklistItem) =>
  [item.label, item.spec].map(value => normalizeWhitespace(String(value || ''))).filter(Boolean).join(' | ');

const dedupeReferenceImages = (images: ReferenceImageHit[] = []) => {
  const byId = new Map<string, ReferenceImageHit>();
  images.forEach(image => {
    const imageId = String(image?.id || '').trim();
    if (!imageId || byId.has(imageId)) return;
    byId.set(imageId, image);
  });
  return Array.from(byId.values());
};

const findNonOverlappingPhraseRange = (
  text: string,
  phrase: string,
  occupied: Array<{ start: number; end: number }>
) => {
  const source = String(text || '');
  const target = String(phrase || '').trim();
  if (!source || !target) return null;
  const lowerSource = source.toLowerCase();
  const lowerTarget = target.toLowerCase();
  let fromIndex = 0;
  while (fromIndex < lowerSource.length) {
    const start = lowerSource.indexOf(lowerTarget, fromIndex);
    if (start < 0) return null;
    const end = start + target.length;
    const overlaps = occupied.some(range => start < range.end && end > range.start);
    if (!overlaps) return { start, end };
    fromIndex = start + 1;
  }
  return null;
};

const toDisplayText = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value.toString();
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return null;
};

const formatMeasurementPlaceholder = (capture: StepCapture) =>
  capture.units ? `Enter value (${capture.units})` : 'Enter measured value';

const formatMeasurementHint = (capture: StepCapture): string | null => {
  const low = toDisplayText(capture.expectedLow);
  const high = toDisplayText(capture.expectedHigh);
  const target = toDisplayText(capture.target);
  const details: string[] = [];
  if (low && high) {
    details.push(`Expected ${low}-${high}${capture.units ? ` ${capture.units}` : ''}`);
  } else if (low) {
    details.push(`Minimum ${low}${capture.units ? ` ${capture.units}` : ''}`);
  } else if (high) {
    details.push(`Maximum ${high}${capture.units ? ` ${capture.units}` : ''}`);
  } else if (target) {
    details.push(`Target ${target}${capture.units ? ` ${capture.units}` : ''}`);
  }
  if (capture.method) details.push(`Method: ${capture.method}`);
  return details.length > 0 ? details.join(' | ') : null;
};

function buildProtocolWarningMessage(availability?: ProtocolAvailability | null): string {
  const base =
    availability?.message ||
    'No active protocol is available for this task. You can continue manually, or close the task and activate a protocol in Documents.';
  const savedRunText =
    availability?.hasSavedRun || Number(availability?.activeStepCount || 0) > 0
      ? 'Saved checklist/progress from an earlier task run may still be shown. Continuing will not reload inactive protocol content.'
      : 'No checklist steps were loaded. You can still record notes/status and save manual progress.';
  return `${base}\n\n${savedRunText}`;
}

const BRAND_TEXT = '#123F43';
const BRAND_MUTED = '#64716F';
const BRAND_SURFACE = '#F2EEE7';
const BRAND_SURFACE_SOFT = '#FBF8F2';
const BRAND_BORDER = '#DDD4C8';
const BRAND_CARD = '#FFFDF9';
const BRAND_TEXT_SOFT = 'rgba(18, 63, 67, 0.08)';
const BRAND_TEXT_SOFT_BORDER = 'rgba(18, 63, 67, 0.18)';
const BRAND_ACCENT = '#D99A33';
const BRAND_ACCENT_SOFT = '#F6E7C9';
const BRAND_ACCENT_TEXT = '#8A611E';
const BRAND_SUCCESS_SOFT = '#E7F2EC';
const BRAND_SUCCESS_BORDER = '#BDD4C7';
const BRAND_SUCCESS_TEXT = '#25624D';
const BRAND_DANGER_SOFT = '#FDEDEC';
const BRAND_DANGER_BORDER = '#E7B9B4';
const BRAND_DANGER_TEXT = '#A63B32';

const createPartUsageId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `maintenance-part-${crypto.randomUUID()}`;
  }
  const randomSegment = Math.random().toString(36).slice(2, 10);
  const timestampSegment = Date.now().toString(36);
  return `maintenance-part-${randomSegment}-${timestampSegment}`;
};

const createEmptyPartDraft = (): PartDraft => ({ ...EMPTY_PART_DRAFT });

const createStepAttachmentId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `checkup-attachment-${crypto.randomUUID()}`;
  }
  const randomSegment = Math.random().toString(36).slice(2, 10);
  const timestampSegment = Date.now().toString(36);
  return `checkup-attachment-${randomSegment}-${timestampSegment}`;
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Unable to process file.'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('Unable to read file.'));
    reader.readAsDataURL(file);
  });

const normalizePartQuantity = (value: unknown): number | null => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric > 0 ? numeric : null;
};

const normalizePartUsage = (value: unknown, fallbackId?: string): PartUsage | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const entry = value as Record<string, unknown>;
  const partNumber =
    typeof entry.partNumber === 'string' ? entry.partNumber.trim() : '';
  const description =
    typeof entry.description === 'string' ? entry.description.trim() : '';
  const batchNumber =
    typeof entry.batchNumber === 'string' ? entry.batchNumber.trim() : '';
  if (!partNumber && !description) {
    return null;
  }
  return {
    id:
      typeof entry.id === 'string' && entry.id.trim()
        ? entry.id.trim()
        : fallbackId || createPartUsageId(),
    partNumber,
    description,
    batchNumber,
    quantity: normalizePartQuantity(entry.quantity ?? entry.qty ?? null),
    source: typeof entry.source === 'string' ? entry.source.trim() || null : null,
    catalogType: typeof entry.catalogType === 'string' ? entry.catalogType.trim() || null : null,
    kitId: typeof entry.kitId === 'string' ? entry.kitId.trim() || null : null,
    kitNumber: typeof entry.kitNumber === 'string' ? entry.kitNumber.trim() || null : null
  };
};

const getReferenceKitPartNumber = (kit: ReferenceKitSummary | null): string => {
  if (!kit) return '';
  return String(kit.kitNumber || kit.partNumber || kit.sku || kit.number || kit.code || '').trim();
};

const getReferenceKitDescription = (kit: ReferenceKitSummary | null): string => {
  if (!kit) return '';
  return String(kit.name || kit.description || '').trim();
};

const normalizeReferenceKit = (value: unknown): ReferenceKitSummary | null => {
  if (!value || typeof value !== 'object') return null;
  const entry = value as Record<string, unknown>;
  const kitNumber = String(entry.kitNumber || entry.partNumber || entry.sku || entry.number || entry.code || '').trim();
  const name = String(entry.name || entry.description || '').trim();
  if (!kitNumber && !name) return null;
  const componentCount =
    typeof entry.componentCount === 'number' && Number.isFinite(entry.componentCount)
      ? entry.componentCount
      : Array.isArray(entry.components)
      ? entry.components.length
      : null;
  return {
    id: typeof entry.id === 'string' ? entry.id.trim() || null : null,
    kitId:
      typeof entry.kitId === 'string'
        ? entry.kitId.trim() || null
        : typeof entry.id === 'string'
        ? entry.id.trim() || null
        : null,
    kitNumber,
    sku: typeof entry.sku === 'string' ? entry.sku.trim() || null : null,
    partNumber: typeof entry.partNumber === 'string' ? entry.partNumber.trim() || null : null,
    number: typeof entry.number === 'string' ? entry.number.trim() || null : null,
    code: typeof entry.code === 'string' ? entry.code.trim() || null : null,
    name,
    description: typeof entry.description === 'string' ? entry.description.trim() || null : null,
    componentCount,
    components: Array.isArray(entry.components) ? entry.components : null
  };
};

const parseChecklistLabel = (label: string): { number: string; description: string } => {
  const trimmed = String(label || '').trim().replace(/\s+/g, ' ');
  const match = trimmed.match(/^(\d+(?:\.\d+)*)\s+(.*)$/);
  if (match) {
    return {
      number: match[1].trim(),
      description: match[2].trim()
    };
  }
  return { number: '', description: trimmed };
};

const buildDeviceExpertContextOptions = (
  items: CheckupChecklistItem[],
  stepId: string
): DeviceExpertContextOption[] =>
  items.map(item => {
    const parsed = parseChecklistLabel(item.label);
    return {
      key: `${stepId}:${item.id}`,
      stepId,
      number: parsed.number,
      description: parsed.description || item.label,
      label: item.label
    };
  });

const formatDeviceExpertText = (value: string): string => {
  const text = String(value || '').replace(/\r\n/g, '\n').trim();
  if (!text) return '';
  const withBreaks = text
    .replace(/\s+(\d+\))/g, '\n$1')
    .replace(/\s+-\s+/g, '\n- ');
  return withBreaks.replace(/\n{3,}/g, '\n\n').trim();
};

const layoutStyles: Record<string, React.CSSProperties> = {
  page: {
    background: BRAND_SURFACE,
    minHeight: '100vh',
    padding: '2.5rem 3rem 4rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.75rem',
    maxWidth: '1600px',
    margin: '0 auto',
    color: BRAND_TEXT,
    width: '100%',
    boxSizing: 'border-box'
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '0.75rem'
  },
  backLink: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    padding: '0.7rem 1rem',
    color: BRAND_TEXT,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  saveMeta: {
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  buttonPrimary: {
    background: BRAND_TEXT,
    color: '#ffffff',
    border: `1px solid ${BRAND_TEXT}`,
    borderRadius: '0.8rem',
    padding: '0.7rem 1.2rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 16px 24px rgba(18, 63, 67, 0.18)'
  },
  buttonSecondary: {
    background: '#ffffff',
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    padding: '0.7rem 1.2rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.7rem',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  card: {
    background: BRAND_CARD,
    borderRadius: '1rem',
    padding: '1.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    boxShadow: '0 22px 48px rgba(18, 63, 67, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '1rem'
  },
  fieldLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    textTransform: 'uppercase' as const
  },
  fieldValue: {
    fontSize: '1rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  intervalPickerRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '0.75rem'
  },
  intervalPickerLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: BRAND_MUTED,
    textTransform: 'uppercase' as const
  },
  intervalSelect: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.55rem 0.85rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: BRAND_TEXT,
    background: '#ffffff'
  },
  mutedCard: {
    borderRadius: '1rem',
    border: `1px dashed ${BRAND_BORDER}`,
    padding: '1.25rem',
    background: BRAND_SURFACE_SOFT,
    color: BRAND_MUTED,
    fontWeight: 500
  },
  section: {
    background: BRAND_CARD,
    borderRadius: '1rem',
    padding: '1.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    boxShadow: '0 18px 40px rgba(18, 63, 67, 0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  statusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1.5rem',
    flexWrap: 'wrap' as const
  },
  statusLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem',
    minWidth: 0,
    flex: 1
  },
  statusChipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  statusChip: {
    borderRadius: '9999px',
    padding: '0.45rem 1.1rem',
    fontWeight: 700,
    fontSize: '0.85rem'
  },
  statusMetaRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '1.25rem'
  },
  statusNote: {
    fontSize: '0.85rem',
    color: BRAND_MUTED,
    marginTop: '0.35rem'
  },
  expertSection: {
    background: 'linear-gradient(180deg, #F7F3EA 0%, #F1F6F3 100%)',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    borderRadius: '1rem',
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    boxShadow: '0 18px 34px -30px rgba(18, 63, 67, 0.26)'
  },
  expertHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap' as const
  },
  expertHeaderCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    flex: '1 1 20rem'
  },
  expertTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  expertSub: {
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  expertGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '0.85rem'
  },
  expertField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem'
  },
  expertLabel: {
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: BRAND_MUTED
  },
  expertSelect: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.7rem 0.85rem',
    width: '100%',
    fontSize: '0.95rem',
    background: '#FCFBF7',
    color: BRAND_TEXT
  },
  expertInput: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.8rem 0.95rem',
    width: '100%',
    fontSize: '0.95rem',
    background: '#ffffff',
    color: BRAND_TEXT
  },
  expertHint: {
    color: BRAND_MUTED,
    fontSize: '0.85rem'
  },
  expertActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  },
  expertMessages: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem'
  },
  expertMessage: {
    borderRadius: '0.9rem',
    padding: '0.8rem 0.95rem',
    lineHeight: 1.55,
    fontSize: '0.93rem',
    whiteSpace: 'pre-wrap' as const
  },
  expertAssistant: {
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT
  },
  expertUser: {
    background: BRAND_TEXT_SOFT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    color: BRAND_TEXT
  },
  expertError: {
    borderRadius: '0.8rem',
    padding: '0.75rem 0.9rem',
    background: BRAND_DANGER_SOFT,
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    color: BRAND_DANGER_TEXT,
    fontWeight: 600
  },
  statusMetaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
  },
  textInput: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.45rem 0.85rem',
    minWidth: '10rem',
    fontSize: '0.95rem',
    fontWeight: 500,
    color: BRAND_TEXT,
    background: '#ffffff'
  },
  textInputDisabled: {
    background: BRAND_SURFACE_SOFT,
    color: BRAND_MUTED,
    cursor: 'not-allowed'
  },
  statusRight: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    minWidth: '15rem',
    alignItems: 'flex-end'
  },
  statusRightLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    color: BRAND_MUTED,
    letterSpacing: '0.08em'
  },
  primaryActions: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.75rem',
    justifyContent: 'flex-end'
  },
  statusActionButton: {
    borderRadius: '0.8rem',
    border: '1px solid transparent',
    padding: '0.7rem 1.5rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.8rem',
    minWidth: '10.5rem'
  },
  statusActionComplete: {
    background: BRAND_TEXT,
    borderColor: BRAND_TEXT,
    color: '#ffffff',
    boxShadow: '0 16px 28px rgba(18, 63, 67, 0.18)'
  },
  statusActionHold: {
    background: BRAND_CARD,
    borderColor: BRAND_ACCENT,
    color: BRAND_ACCENT_TEXT,
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  statusActionReopen: {
    background: '#ffffff',
    borderColor: BRAND_BORDER,
    color: BRAND_MUTED,
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  statusActionDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  reportButton: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_TEXT}`,
    padding: '0.75rem 1.5rem',
    fontWeight: 700,
    fontSize: '0.95rem',
    background: BRAND_TEXT,
    color: '#ffffff',
    cursor: 'pointer',
    alignSelf: 'stretch',
    minWidth: '16rem',
    minHeight: '2.8rem',
    boxShadow: '0 16px 24px rgba(18, 63, 67, 0.18)'
  },
  reportButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  textarea: {
    borderRadius: '0.9rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.85rem',
    minHeight: '6rem',
    width: '100%',
    fontSize: '0.95rem',
    color: BRAND_TEXT,
    resize: 'vertical' as const,
    background: '#ffffff'
  },
  reportCard: {
    background: BRAND_CARD,
    borderRadius: '1rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '1.5rem',
    boxShadow: '0 18px 40px rgba(18, 63, 67, 0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  reportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap' as const
  },
  reportTitle: {
    fontSize: '1.1rem',
    fontWeight: 800,
    color: BRAND_TEXT
  },
  reportMeta: {
    fontSize: '0.85rem',
    color: BRAND_MUTED,
    marginTop: '0.2rem'
  },
  reportActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  },
  reportBody: {
    borderRadius: '0.9rem',
    border: `1px dashed ${BRAND_BORDER}`,
    padding: '1rem',
    whiteSpace: 'pre-wrap' as const,
    lineHeight: 1.55,
    color: BRAND_TEXT,
    background: '#ffffff'
  },
  reportTextarea: {
    borderRadius: '0.9rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.85rem',
    minHeight: '16rem',
    width: '100%',
    fontSize: '0.95rem',
    color: BRAND_TEXT,
    resize: 'vertical' as const,
    background: '#ffffff'
  },
  reportEditActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  },
  reportSecondaryButton: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#ffffff',
    color: BRAND_TEXT,
    padding: '0.65rem 1.2rem',
    fontWeight: 700,
    cursor: 'pointer'
  },
  reportPrimaryButton: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_TEXT}`,
    background: BRAND_TEXT,
    color: '#ffffff',
    padding: '0.65rem 1.2rem',
    fontWeight: 700,
    cursor: 'pointer'
  },
  reportError: {
    color: BRAND_DANGER_TEXT,
    fontWeight: 700,
    fontSize: '0.9rem'
  }
};

const checklistStyles: Record<string, React.CSSProperties> = {
  section: {
    background: BRAND_CARD,
    borderRadius: '1rem',
    padding: '1.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    boxShadow: '0 18px 40px rgba(18, 63, 67, 0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap' as const,
    gap: '0.75rem'
  },
  sectionSub: {
    fontSize: '0.9rem',
    color: BRAND_MUTED
  },
  error: {
    background: BRAND_DANGER_SOFT,
    color: BRAND_DANGER_TEXT,
    borderRadius: '0.85rem',
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    padding: '0.75rem 0.95rem',
    fontWeight: 600
  },
  loading: {
    padding: '0.75rem 0.5rem',
    color: BRAND_MUTED,
    fontStyle: 'italic'
  },
  empty: {
    padding: '0.75rem 0.5rem',
    color: BRAND_MUTED
  },
  stepCard: {
    borderRadius: '1rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_SURFACE_SOFT,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem',
    boxShadow: '0 14px 28px rgba(18, 63, 67, 0.05)'
  },
  stepHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap' as const
  },
  stepTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: BRAND_TEXT,
    display: 'flex',
    gap: '0.35rem',
    alignItems: 'flex-start'
  },
  stepIndex: {
    color: BRAND_TEXT,
    fontWeight: 800
  },
  stepSummary: {
    color: BRAND_MUTED,
    marginTop: '0.35rem',
    maxWidth: '52rem',
    lineHeight: 1.45
  },
  stepActions: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const
  },
  flagButton: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#ffffff',
    color: BRAND_TEXT,
    padding: '0.55rem 0.95rem',
    fontWeight: 700,
    cursor: 'not-allowed',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  toggleButton: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#ffffff',
    color: BRAND_TEXT,
    padding: '0.55rem 0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  checklist: {
    borderRadius: '0.75rem',
    border: `1px dashed ${BRAND_BORDER}`,
    padding: '0.85rem 1rem',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem'
  },
  checklistItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    color: BRAND_TEXT
  },
  checklistToggle: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-start',
    color: BRAND_TEXT
  },
  checklistLabel: {
    fontWeight: 600
  },
  checklistPrimaryText: {
    fontWeight: 600
  },
  checklistDetailText: {
    fontWeight: 400,
    color: BRAND_MUTED
  },
  checklistSpec: {
    color: BRAND_MUTED,
    fontSize: '0.9rem',
    marginTop: '0.15rem',
    lineHeight: 1.4
  },
  inlineImageTextLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: BRAND_TEXT,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    cursor: 'pointer',
    font: 'inherit',
    textAlign: 'left'
  },
  captureRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    paddingLeft: '1.7rem'
  },
  captureInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    maxWidth: '16rem'
  },
  captureLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  captureLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  captureDetailsToggle: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    color: BRAND_TEXT,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 600
  },
  captureInput: {
    borderRadius: '0.6rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.45rem 0.6rem',
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    background: '#ffffff'
  },
  captureInputValid: {
    borderColor: BRAND_SUCCESS_BORDER,
    background: BRAND_SUCCESS_SOFT
  },
  captureInputWarning: {
    borderColor: BRAND_ACCENT,
    background: BRAND_ACCENT_SOFT
  },
  captureInputInvalid: {
    borderColor: BRAND_DANGER_BORDER,
    background: BRAND_DANGER_SOFT
  },
  captureHint: {
    fontSize: '0.75rem',
    color: BRAND_MUTED
  },
  captureFeedback: {
    fontSize: '0.75rem',
    fontWeight: 700
  },
  notesCard: {
    borderRadius: '0.85rem',
    border: `1px dashed ${BRAND_BORDER}`,
    padding: '0.9rem 1rem',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  partsCard: {
    borderRadius: '0.85rem',
    border: `1px dashed ${BRAND_BORDER}`,
    padding: '0.9rem 1rem',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  partsTitle: {
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    textTransform: 'uppercase' as const
  },
  referenceKitChipRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    marginBottom: '0.35rem'
  },
  referenceKitChipLabel: {
    fontSize: '0.82rem',
    color: BRAND_MUTED,
    fontWeight: 600
  },
  referenceKitChip: {
    borderRadius: '9999px',
    border: `1px solid ${BRAND_SUCCESS_BORDER}`,
    background: BRAND_SUCCESS_SOFT,
    color: BRAND_TEXT,
    fontWeight: 700,
    padding: '0.35rem 0.8rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const
  },
  partsTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem'
  },
  partsRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(8rem, 1.1fr) minmax(12rem, 1.7fr) minmax(10rem, 1.2fr) minmax(4.5rem, 0.7fr) auto',
    gap: '0.5rem',
    alignItems: 'center'
  },
  partsHeaderRow: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: BRAND_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em'
  },
  partCellValue: {
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    minWidth: 0,
    overflowWrap: 'anywhere'
  },
  partCellInput: {
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.55rem 0.7rem',
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    background: '#ffffff',
    width: '100%',
    minWidth: 0
  },
  partActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.45rem',
    flexWrap: 'wrap' as const
  },
  partAddButton: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_TEXT}`,
    background: BRAND_TEXT,
    color: '#ffffff',
    fontWeight: 700,
    padding: '0.55rem 0.9rem',
    cursor: 'pointer',
    boxShadow: '0 14px 24px rgba(18, 63, 67, 0.18)'
  },
  partScanButton: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_TEXT}`,
    background: '#ffffff',
    color: BRAND_TEXT,
    fontWeight: 700,
    padding: '0.55rem 0.9rem',
    cursor: 'pointer',
    boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
  },
  partRemoveButton: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    background: BRAND_DANGER_SOFT,
    color: BRAND_DANGER_TEXT,
    fontWeight: 700,
    padding: '0.45rem 0.75rem',
    cursor: 'pointer'
  },
  scanMessage: {
    borderRadius: '0.7rem',
    padding: '0.55rem 0.75rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    lineHeight: 1.4
  },
  scanMessageError: {
    background: BRAND_DANGER_SOFT,
    color: BRAND_DANGER_TEXT
  },
  scanMessageSuccess: {
    background: BRAND_SUCCESS_SOFT,
    color: BRAND_SUCCESS_TEXT
  },
  notesLabelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '0.5rem'
  },
  notesTextarea: {
    borderRadius: '0.7rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.65rem 0.75rem',
    minHeight: '4.5rem',
    width: '100%',
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    background: '#ffffff',
    resize: 'vertical' as const
  },
  attachmentToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap' as const
  },
  addAttachmentButton: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_TEXT}`,
    background: BRAND_TEXT,
    color: '#ffffff',
    fontWeight: 700,
    padding: '0.55rem 0.9rem',
    cursor: 'pointer',
    boxShadow: '0 14px 24px rgba(18, 63, 67, 0.18)'
  },
  attachmentList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem'
  },
  attachmentPill: {
    borderRadius: '9999px',
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_SURFACE_SOFT,
    padding: '0.3rem 0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontSize: '0.85rem',
    color: BRAND_TEXT
  },
  removeAttachmentButton: {
    border: 'none',
    background: 'transparent',
    color: BRAND_DANGER_TEXT,
    cursor: 'pointer',
    fontWeight: 700
  },
  previewOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 1200
  },
  previewModal: {
    backgroundColor: BRAND_CARD,
    borderRadius: '8px',
    padding: '0.75rem',
    width: 'min(94vw, 1400px)',
    height: '90vh',
    maxHeight: '940px',
    border: `1px solid ${BRAND_BORDER}`,
    boxShadow: '0 25px 65px rgba(15, 23, 42, 0.35)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem',
    overflow: 'hidden'
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem'
  },
  previewTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    minWidth: 0
  },
  previewCounter: {
    color: BRAND_MUTED,
    fontSize: '0.85rem'
  },
  previewCloseButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    color: BRAND_TEXT,
    borderRadius: '0.5rem',
    padding: '0.4rem 0.75rem',
    fontWeight: 700,
    cursor: 'pointer'
  },
  previewControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
    flexWrap: 'wrap',
    borderTop: `1px solid ${BRAND_BORDER}`,
    paddingTop: '0.55rem'
  },
  previewControlButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    borderRadius: '6px',
    padding: '0.32rem 0.58rem',
    fontWeight: 700,
    color: BRAND_TEXT,
    cursor: 'pointer',
    minWidth: '2.35rem'
  },
  previewZoomSlider: {
    width: 'min(34vw, 420px)',
    maxWidth: '100%'
  },
  previewZoomValue: {
    color: BRAND_MUTED,
    fontSize: '0.9rem',
    minWidth: '3rem'
  },
  previewGalleryStrip: {
    display: 'flex',
    gap: '0.45rem',
    overflowX: 'auto',
    paddingBottom: '0.15rem'
  },
  previewGalleryThumbButton: {
    width: '86px',
    height: '60px',
    flex: '0 0 auto',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '6px',
    padding: 0,
    overflow: 'hidden',
    backgroundColor: BRAND_CARD,
    cursor: 'pointer'
  },
  previewGalleryThumbButtonActive: {
    borderColor: BRAND_TEXT,
    boxShadow: '0 0 0 2px rgba(18, 63, 67, 0.18)'
  },
  previewGalleryThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block'
  },
  previewGalleryThumbPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: BRAND_SURFACE_SOFT,
    color: BRAND_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontSize: '0.72rem'
  },
  previewImageStage: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: BRAND_SURFACE_SOFT,
    borderRadius: '6px',
    padding: '0.75rem'
  },
  previewImageCanvas: {
    minWidth: '100%',
    minHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  previewImage: {
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'contain',
    display: 'block',
    backgroundColor: BRAND_SURFACE_SOFT
  },
  previewPlaceholder: {
    width: '100%',
    height: '320px',
    color: BRAND_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: BRAND_SURFACE_SOFT,
    borderRadius: '0.65rem'
  }
};

const dialogStyles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(18, 63, 67, 0.32)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50
  },
  card: {
    background: BRAND_CARD,
    borderRadius: '1.1rem',
    padding: '1.5rem',
    maxWidth: '28rem',
    width: '90%',
    boxShadow: '0 30px 70px rgba(18, 63, 67, 0.16)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem'
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  subtitle: {
    color: BRAND_MUTED,
    lineHeight: 1.4
  },
  textarea: {
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.6rem 0.8rem',
    resize: 'vertical' as const,
    minHeight: '5rem',
    fontSize: '0.95rem',
    color: BRAND_TEXT,
    background: '#ffffff'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem'
  },
  error: {
    color: BRAND_DANGER_TEXT,
    fontWeight: 600
  }
};

const formatDateTime = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const getProblemDescription = (caseSummary: MaintenanceCaseSummary | null): string =>
  (caseSummary?.problemDescription && caseSummary.problemDescription.trim()) ||
  'No maintenance context provided.';

const getCaseNumber = (caseSummary: MaintenanceCaseSummary | null): string =>
  caseSummary?.caseCode ||
  caseSummary?.caseNumber ||
  caseSummary?.id ||
  'Unknown case';

const getCustomerName = (caseSummary: MaintenanceCaseSummary | null): string =>
  caseSummary?.customer?.name ||
  caseSummary?.customer?.email ||
  'Unknown customer';

const getDeviceLabel = (caseSummary: MaintenanceCaseSummary | null): string => {
  const device = caseSummary?.device;
  if (!device) return 'No device linked';
  const modelLabel = device.modelNumber || device.modelName || device.manufacturer || 'Device';
  if (device.serialNumber) {
    return `${modelLabel} (Serial ${device.serialNumber})`;
  }
  return modelLabel;
};

const getLocationName = (caseSummary: MaintenanceCaseSummary | null): string => {
  const location = caseSummary?.location;
  return location?.name || 'Location not captured';
};

const getContactName = (caseSummary: MaintenanceCaseSummary | null): string => {
  const contact = caseSummary?.contact;
  if (contact) {
    const parts = [contact.name, contact.email, contact.phone].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(' | ');
    }
  }
  if (caseSummary?.customer?.name) return caseSummary.customer.name;
  return 'No contact on file';
};

const getSlaHeroSummary = (caseSummary: MaintenanceCaseSummary | null): { value: string; note: string } => {
  const summary = caseSummary?.device?.activeSlaSummary;
  if (summary?.active) {
    return {
      value: summary.label?.trim() || summary.brandingName?.trim() || 'SLA',
      note: summary.note?.trim() || 'Active device SLA'
    };
  }
  return {
    value: 'No active SLA',
    note: 'No active device SLA'
  };
};


export default function CheckUpTask({ variant = 'checkup' }: CheckUpTaskProps) {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const isPreventativeMaintenance = variant === 'preventative';
  const taskTitle = isPreventativeMaintenance ? 'Preventative Maintenance' : 'Maintenance Check Up';
  const defaultReferenceType = isPreventativeMaintenance ? 'PREVENTATIVE' : 'CHECKUP';
  const loadingMessage = isPreventativeMaintenance
    ? 'Loading preventative maintenance workspace...'
    : 'Loading maintenance check up workspace...';
  const loadErrorMessage = isPreventativeMaintenance
    ? 'Failed to load preventative maintenance workspace.'
    : 'Failed to load maintenance workspace.';
  const maintenanceApiForVariant = useMemo(
    () => ({
      get: (targetCaseId: string) =>
        isPreventativeMaintenance
          ? maintenanceApi.getPreventative(targetCaseId)
          : maintenanceApi.getCheckup(targetCaseId),
      refresh: (
        targetCaseId: string,
        options: { interval?: string; revisionId?: string } = {}
      ) =>
        isPreventativeMaintenance
          ? maintenanceApi.refreshPreventative(targetCaseId, options)
          : maintenanceApi.refreshCheckup(targetCaseId, options),
      clear: (targetCaseId: string, options: { interval?: string } = {}) =>
        isPreventativeMaintenance
          ? maintenanceApi.clearPreventative(targetCaseId, options)
          : maintenanceApi.clearCheckup(targetCaseId, options),
      save: (targetCaseId: string, payload: Parameters<typeof maintenanceApi.saveCheckup>[1]) =>
        isPreventativeMaintenance
          ? maintenanceApi.savePreventative(targetCaseId, payload)
          : maintenanceApi.saveCheckup(targetCaseId, payload),
      generateReport: (targetCaseId: string) =>
        isPreventativeMaintenance
          ? maintenanceApi.generatePreventativeReport(targetCaseId)
          : maintenanceApi.generateCheckupReport(targetCaseId),
      saveReport: (targetCaseId: string, summary: string) =>
        isPreventativeMaintenance
          ? maintenanceApi.savePreventativeReport(targetCaseId, summary)
          : maintenanceApi.saveCheckupReport(targetCaseId, summary),
      createServiceReportDraft: (targetCaseId: string) =>
        isPreventativeMaintenance
          ? maintenanceApi.createPreventativeServiceReportDraft(targetCaseId)
          : maintenanceApi.createCheckupServiceReportDraft(targetCaseId),
      saveServiceReport: (targetCaseId: string, document: Record<string, unknown>) =>
        isPreventativeMaintenance
          ? maintenanceApi.savePreventativeServiceReport(targetCaseId, document)
          : maintenanceApi.saveCheckupServiceReport(targetCaseId, document),
      sendServiceReport: (targetCaseId: string, reportId: string) =>
        isPreventativeMaintenance
          ? maintenanceApi.sendPreventativeServiceReport(targetCaseId, reportId)
          : maintenanceApi.sendCheckupServiceReport(targetCaseId, reportId)
    }),
    [isPreventativeMaintenance]
  );

  const [workspaceData, setWorkspaceData] = useState<MaintenanceWorkspacePayload | null>(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const [clearing, setClearing] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const [taskStatus, setTaskStatus] = useState<MaintenanceStatus>('PLANNED');

  const [statusReason, setStatusReason] = useState('');

  const [totalTimeHours, setTotalTimeHours] = useState('');

  const [taskNotes, setTaskNotes] = useState('');

  const [completedSteps, setCompletedSteps] = useState(0);

  const [totalSteps, setTotalSteps] = useState(0);

  const [deviceExpertOpen, setDeviceExpertOpen] = useState(true);
  const [deviceExpertStepId, setDeviceExpertStepId] = useState('');
  const [deviceExpertContextKey, setDeviceExpertContextKey] = useState('');
  const [deviceExpertQuestion, setDeviceExpertQuestion] = useState('');
  const [deviceExpertMessages, setDeviceExpertMessages] = useState<DeviceExpertMessage[]>([]);
  const [deviceExpertLoading, setDeviceExpertLoading] = useState(false);
  const [deviceExpertError, setDeviceExpertError] = useState<string | null>(null);
  const [deviceExpertConversationId, setDeviceExpertConversationId] = useState<string | null>(null);

  const [referenceMeta, setReferenceMeta] = useState<{
    revisionId?: string | null;
    maintenanceInterval?: string | null;
    type?: string | null;
  } | null>(null);
  const [referenceKit, setReferenceKit] = useState<ReferenceKitSummary | null>(null);
  const [availableIntervals, setAvailableIntervals] = useState<string[]>([]);
  const [selectedInterval, setSelectedInterval] = useState("");

  const [checkupSteps, setCheckupSteps] = useState<CheckupStep[]>([]);
  const [previewImages, setPreviewImages] = useState<ReferenceImageHit[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(100);
  const [captureDetailsOpen, setCaptureDetailsOpen] = useState<Record<string, boolean>>({});

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const partScanInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const missingReferenceNoticeKeyRef = useRef<string | null>(null);
  const [partDrafts, setPartDrafts] = useState<Record<string, PartDraft>>({});
  const [partScanStates, setPartScanStates] = useState<Record<string, StepPartScanState>>({});
  const [partScanMessages, setPartScanMessages] = useState<Record<string, string | null>>({});

  const [statusDialog, setStatusDialog] = useState<{
    mode: MaintenanceStatus;
    reason: string;
    requireReason: boolean;
  } | null>(null);

  const [statusDialogError, setStatusDialogError] = useState<string | null>(null);
  const [reportSummary, setReportSummary] = useState<string | null>(null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState<string | null>(null);
  const [reportIsCustom, setReportIsCustom] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportEditing, setReportEditing] = useState(false);
  const [reportDraft, setReportDraft] = useState('');
  const [reportEditError, setReportEditError] = useState<string | null>(null);
  const [serviceReportModalOpen, setServiceReportModalOpen] = useState(false);
  const [serviceReportDraft, setServiceReportDraft] = useState<ServiceReportDocument | null>(null);
  const [serviceReportLoading, setServiceReportLoading] = useState(false);
  const [serviceReportSaving, setServiceReportSaving] = useState(false);
  const [serviceReportSending, setServiceReportSending] = useState(false);
  const [serviceReportError, setServiceReportError] = useState<string | null>(null);
  const [serviceReportMode, setServiceReportMode] = useState<'create' | 'view'>('create');
  const previewImage = previewImages[previewImageIndex] || null;
  const previewImageCount = previewImages.length;



  const applyWorkspace = useCallback(

    (payload: MaintenanceWorkspacePayload, options: { savedAt?: string | null } = {}) => {

      setWorkspaceData(payload);

      setReferenceMeta(payload.reference ?? null);
      setReferenceKit(normalizeReferenceKit(payload.referenceKit ?? null));

      const rawIntervalOptions = payload.intervals?.options;
      const intervalOptions = Array.isArray(rawIntervalOptions) ? rawIntervalOptions : [];
      const intervalLabels = Array.from(
        new Set(
          intervalOptions
            .map(option => (option?.interval && option.interval.trim()) || '')
            .filter(Boolean)
        )
      );
      setAvailableIntervals(intervalLabels);
      const intervalSelection = payload.intervals?.selected;
      const resolvedInterval =
        (typeof intervalSelection === 'string' && intervalSelection.trim()) ||
        intervalLabels[0] ||
        '';
      setSelectedInterval(resolvedInterval);

      const mappedSteps: CheckupStep[] = Array.isArray(payload.steps)

        ? payload.steps.map(step => ({

            id: step.id,

            label: step.label,

            summary: step.summary || null,

            checklist: Array.isArray(step.checklist)

              ? step.checklist.map(item => ({

                  id: item.id,

                  label: item.label,

                  spec: item.spec || null,

                  completed: Boolean(item.completed),

                  sourceNodeIds: Array.isArray(item.sourceNodeIds) ? item.sourceNodeIds : [],

                  capture:
                    item.capture && typeof item.capture === 'object'
                      ? item.capture
                      : null,

                  captureValue:
                    item.captureValue === null || item.captureValue === undefined
                      ? ''
                      : String(item.captureValue),

                  reference: item.reference || null

                }))

              : [],

            partsUsed: Array.isArray(step.partsUsed)

              ? step.partsUsed

                  .map((part, partIndex) => normalizePartUsage(part, `${step.id}-part-${partIndex + 1}`))

                  .filter((part): part is PartUsage => part !== null)

              : [],

            collapsed: Boolean(step.collapsed),

            notes: step.notes || '',

            attachments: Array.isArray(step.attachments)

              ? step.attachments.map(attachment => ({

                  id: attachment.id,

                  name: attachment.name,

                  size: attachment.size,

                  dataUrl: attachment.dataUrl,

                  addedAt: attachment.addedAt

                }))

              : [],

            notApplicable: Boolean(step.notApplicable),

            referenceImages: Array.isArray(step.referenceImages) ? step.referenceImages : [],

            referenceImageBlocks: Array.isArray(step.referenceImageBlocks)
              ? step.referenceImageBlocks
              : []

          }))

        : [];

      setCheckupSteps(mappedSteps);
      setPartDrafts({});
      setPartScanStates({});
      setPartScanMessages({});
      setDeviceExpertError(null);

      const normalizedStatus = (payload.task?.status || 'PLANNED').toUpperCase() as MaintenanceStatus;

      setTaskStatus(

        VALID_MAINTENANCE_STATUSES.includes(normalizedStatus) ? normalizedStatus : 'PLANNED'

      );

      setTaskNotes(typeof payload.run?.notes === 'string' ? payload.run.notes : '');

      const runMetadata = (payload.run?.metadata ?? {}) as Record<string, unknown>;

      const timeMinutes = Number(runMetadata.totalTimeMinutes);

      setTotalTimeHours(

        !Number.isNaN(timeMinutes) && timeMinutes > 0

          ? String(Math.round((timeMinutes / 60) * 100) / 100)

          : ''

      );

      setStatusReason(

        typeof runMetadata.statusReason === 'string' ? runMetadata.statusReason : ''

      );

      const savedReportSummary =
        typeof runMetadata.reportSummary === 'string' ? runMetadata.reportSummary.trim() : '';
      setReportSummary(savedReportSummary || null);
      setReportGeneratedAt(
        typeof runMetadata.reportGeneratedAt === 'string' ? runMetadata.reportGeneratedAt : null
      );
      setReportIsCustom(typeof runMetadata.reportIsCustom === 'boolean' ? runMetadata.reportIsCustom : false);
      setReportLoading(false);
      setReportEditing(false);
      setReportDraft('');
      setReportEditError(null);
      setServiceReportError(null);

      const savedTimestamp =

        options.savedAt ??

        (typeof runMetadata.lastUpdatedAt === 'string'

          ? runMetadata.lastUpdatedAt

          : typeof payload.run?.updatedAt === 'string'

          ? payload.run.updatedAt

          : typeof payload.task?.updatedAt === 'string'

          ? payload.task.updatedAt

          : null);

      setLastSavedAt(savedTimestamp || null);

      setHasUnsavedChanges(false);

      const availability = payload.protocolAvailability || null;
      if (availability?.available === false) {
        const intervalHint =
          (typeof availability.maintenanceInterval === 'string' && availability.maintenanceInterval.trim()) ||
          (typeof payload.reference?.maintenanceInterval === 'string' &&
            payload.reference.maintenanceInterval.trim()) ||
          'unspecified interval';
        const noticeKey = `${availability.referenceType || payload.reference?.type || defaultReferenceType}|${availability.reason || 'unavailable'}|${intervalHint}|${payload.run?.id || ''}`;
        if (missingReferenceNoticeKeyRef.current !== noticeKey) {
          missingReferenceNoticeKeyRef.current = noticeKey;
          void showInAppConfirm(buildProtocolWarningMessage(availability), {
            title: 'No active protocol available',
            confirmLabel: 'Continue manually',
            cancelLabel: 'Close task',
            tone: 'danger'
          }).then(confirmed => {
            if (!confirmed) {
              navigate(-1);
            }
          });
        }
        return;
      }

      const revisionId =
        payload.reference?.revisionId && payload.reference.revisionId.trim().length > 0
          ? payload.reference.revisionId.trim()
          : null;
      if (revisionId) {
        missingReferenceNoticeKeyRef.current = null;
        return;
      }

      const intervalHint =
        (typeof payload.reference?.maintenanceInterval === 'string' &&
          payload.reference.maintenanceInterval.trim()) ||
        'unspecified interval';
      const noticeKey = `${payload.reference?.type || defaultReferenceType}|${intervalHint}`;
      if (missingReferenceNoticeKeyRef.current === noticeKey) {
        return;
      }
      missingReferenceNoticeKeyRef.current = noticeKey;
      void showInAppConfirm(
        `${MISSING_PROTOCOL_ALERT_MESSAGE}\n\nNo checklist steps were loaded. You can still record notes/status and save manual progress.`,
        {
          title: 'No active protocol available',
          confirmLabel: 'Continue manually',
          cancelLabel: 'Close task',
          tone: 'danger'
        }
      ).then(confirmed => {
        if (!confirmed) {
          navigate(-1);
        }
      });

    },

    [defaultReferenceType, navigate]

  );



  const loadWorkspace = useCallback(

    async (options: { showSpinner?: boolean } = {}) => {

      if (!caseId) return false;

      if (options.showSpinner) {

        setLoading(true);

      }

      setError(null);

      try {

        const response = await maintenanceApiForVariant.get(caseId);

        const { savedAt, refreshedAt, clearedAt, ...workspacePayload } = response as any;

        const resolvedTimestamp =

          typeof savedAt === 'string'

            ? savedAt

            : typeof refreshedAt === 'string'

            ? refreshedAt

            : typeof clearedAt === 'string'

            ? clearedAt

            : null;

        applyWorkspace(workspacePayload as MaintenanceWorkspacePayload, {

          savedAt: resolvedTimestamp

        });

      } catch (err) {

        console.error(`Failed to load ${taskTitle.toLowerCase()} workspace`, err);

        setError(

          err instanceof Error ? err.message : loadErrorMessage

        );

        setWorkspaceData(null);

        setCheckupSteps([]);
        setReferenceKit(null);

      } finally {

        if (options.showSpinner) {

          setLoading(false);

        }

      }

    },

    [applyWorkspace, caseId, loadErrorMessage, maintenanceApiForVariant, taskTitle]

  );



  useEffect(() => {

    loadWorkspace({ showSpinner: true });

  }, [loadWorkspace]);



  useEffect(() => {

    if (checkupSteps.length === 0) {

      setCompletedSteps(0);

      setTotalSteps(0);

      return;

    }

    let completed = 0;

    let total = 0;

    checkupSteps.forEach(step => {

      const stepTotal = step.checklist.length;

      total += stepTotal;

      if (step.notApplicable) {

        completed += stepTotal;

      } else {

        completed += step.checklist.filter(item => item.completed).length;

      }

    });

    setCompletedSteps(completed);

    setTotalSteps(total);

  }, [checkupSteps]);

  const deviceExpertModelId = workspaceData?.caseSummary?.device?.modelId || null;
  const deviceExpertParentOptions = useMemo<DeviceExpertParentOption[]>(
    () =>
      checkupSteps
        .map((step, index) => ({
          id: step.id,
          sequence: index + 1,
          label: step.label,
          checklistItems: step.checklist
        }))
        .filter(option => option.checklistItems.length > 0),
    [checkupSteps]
  );
  const selectedDeviceExpertParent = useMemo(
    () => deviceExpertParentOptions.find(option => option.id === deviceExpertStepId) || null,
    [deviceExpertParentOptions, deviceExpertStepId]
  );
  const deviceExpertContextOptions = useMemo(
    () =>
      selectedDeviceExpertParent
        ? buildDeviceExpertContextOptions(
            selectedDeviceExpertParent.checklistItems,
            selectedDeviceExpertParent.id
          )
        : [],
    [selectedDeviceExpertParent]
  );
  const selectedDeviceExpertContext = useMemo(
    () => deviceExpertContextOptions.find(option => option.key === deviceExpertContextKey) || null,
    [deviceExpertContextKey, deviceExpertContextOptions]
  );

  useEffect(() => {
    if (!selectedDeviceExpertParent && deviceExpertStepId) {
      setDeviceExpertStepId('');
    }
  }, [deviceExpertStepId, selectedDeviceExpertParent]);

  useEffect(() => {
    if (
      deviceExpertContextKey &&
      !deviceExpertContextOptions.some(option => option.key === deviceExpertContextKey)
    ) {
      setDeviceExpertContextKey('');
    }
  }, [deviceExpertContextKey, deviceExpertContextOptions]);



  const referenceLoading = loading || refreshing || clearing;
  const workspaceReadOnly = workspaceData?.access?.canWrite === false;
  const savedServiceReportStatus = String(workspaceData?.serviceReport?.status || '').trim().toUpperCase();
  const savedServiceReportSent = savedServiceReportStatus === 'SENT';
  const canRebuildServiceReport =
    !workspaceReadOnly &&
    taskStatus === 'COMPLETED' &&
    Boolean(reportSummary?.trim()) &&
    !savedServiceReportSent;
  const assignedLabel =
    workspaceData?.access?.assignedUserName || workspaceData?.access?.assignedUserId || null;
  const referenceKitPart = useMemo(() => {
    const partNumber = getReferenceKitPartNumber(referenceKit);
    if (!referenceKit || !partNumber) return null;
    return {
      partNumber,
      description: getReferenceKitDescription(referenceKit),
      kitId: referenceKit.kitId || referenceKit.id || null,
      componentCount:
        typeof referenceKit.componentCount === 'number'
          ? referenceKit.componentCount
          : Array.isArray(referenceKit.components)
          ? referenceKit.components.length
          : null
    };
  }, [referenceKit]);



  const handleRefresh = useCallback(async () => {

    if (!caseId) return;
    if (workspaceReadOnly) return;

    if (hasUnsavedChanges) {
      const confirmed = await showInAppConfirm(
        'You have unsaved changes. Refreshing will discard them. Continue?',
        {
          title: 'Discard unsaved changes?',
          confirmLabel: 'Discard',
          tone: 'danger'
        }
      );
      if (!confirmed) return;
    }

    setRefreshing(true);

    setError(null);

    try {

      const response = await maintenanceApiForVariant.refresh(caseId, {
        interval: selectedInterval || undefined
      });

      const { refreshedAt, ...workspacePayload } = response as any;

      applyWorkspace(workspacePayload as MaintenanceWorkspacePayload, {

        savedAt: typeof refreshedAt === 'string' ? refreshedAt : null

      });

    } catch (err) {

      console.error('Failed to refresh maintenance workspace', err);

      setError(err instanceof Error ? err.message : 'Failed to refresh maintenance workspace.');

    } finally {

      setRefreshing(false);

    }

  }, [applyWorkspace, caseId, hasUnsavedChanges, maintenanceApiForVariant, selectedInterval, workspaceReadOnly]);



  const handleIntervalChange = useCallback(

    async (value: string) => {

      if (!caseId) return false;
      if (workspaceReadOnly) return;

      const trimmed = value.trim();

      if (!trimmed || trimmed === selectedInterval) return;

      if (hasUnsavedChanges) {
        const confirmed = await showInAppConfirm(
          'Changing the interval will discard unsaved changes. Continue?',
          {
            title: 'Change interval?',
            confirmLabel: 'Change',
            tone: 'danger'
          }
        );
        if (!confirmed) return;
      }

      const previous = selectedInterval;

      setSelectedInterval(trimmed);

      setRefreshing(true);

      setError(null);

      try {

        const response = await maintenanceApiForVariant.refresh(caseId, { interval: trimmed });

        const { refreshedAt, ...workspacePayload } = response as any;

        applyWorkspace(workspacePayload as MaintenanceWorkspacePayload, {

          savedAt: typeof refreshedAt === 'string' ? refreshedAt : null

        });

      } catch (err) {

        console.error('Failed to switch maintenance interval', err);

        setError(

          err instanceof Error ? err.message : 'Failed to load the selected maintenance interval.'

        );

        setSelectedInterval(previous);

      } finally {

        setRefreshing(false);

      }

    },

    [applyWorkspace, caseId, hasUnsavedChanges, maintenanceApiForVariant, selectedInterval, workspaceReadOnly]

  );



  const handleClearProgress = useCallback(async () => {

    if (!caseId) return;
    if (workspaceReadOnly) return;

    if (hasUnsavedChanges) {
      const confirmed = await showInAppConfirm(
        'You have unsaved changes. Clear progress anyway?',
        {
          title: 'Clear progress?',
          confirmLabel: 'Clear',
          tone: 'danger'
        }
      );
      if (!confirmed) return;
    }

    setClearing(true);

    setError(null);

    try {

      const response = await maintenanceApiForVariant.clear(caseId, {
        interval: selectedInterval || undefined
      });

      const { clearedAt, ...workspacePayload } = response as any;

      applyWorkspace(workspacePayload as MaintenanceWorkspacePayload, {

        savedAt: typeof clearedAt === 'string' ? clearedAt : null

      });

    } catch (err) {

      console.error('Failed to clear maintenance workspace', err);

      setError(err instanceof Error ? err.message : 'Failed to clear maintenance workspace.');

    } finally {

      setClearing(false);

    }

  }, [applyWorkspace, caseId, hasUnsavedChanges, maintenanceApiForVariant, selectedInterval, workspaceReadOnly]);


  const buildSavePayload = useCallback(

    (overrides?: { status?: MaintenanceStatus; statusReason?: string }) => {

      if (!workspaceData?.task?.id || !workspaceData?.run?.id) {

        return null;

      }

      const normalizedTotalTime =

        totalTimeHours && !Number.isNaN(Number(totalTimeHours))

          ? Math.round(Number(totalTimeHours) * 60)

          : null;

      const resolvedStatus = overrides?.status ?? taskStatus;

      const resolvedReason = overrides?.statusReason ?? statusReason;
      const existingRunMetadata =
        workspaceData.run?.metadata && typeof workspaceData.run.metadata === 'object'
          ? workspaceData.run.metadata
          : {};
      return {

        taskId: workspaceData.task.id,

        runId: workspaceData.run.id,

        taskStatus: resolvedStatus,

        runNotes: taskNotes,

        runMetadata: {

          ...existingRunMetadata,
          ...(normalizedTotalTime !== null ? { totalTimeMinutes: normalizedTotalTime } : {}),

          ...(resolvedReason ? { statusReason: resolvedReason } : {}),

          ...(selectedInterval ? { maintenanceInterval: selectedInterval } : {}),

          ...(reportSummary ? { reportSummary } : {}),

          ...(reportGeneratedAt ? { reportGeneratedAt } : {}),

          reportIsCustom
        },
        steps: checkupSteps.map(step => {

          const totalItems = step.checklist.length;

          const isComplete =

            step.notApplicable || (totalItems > 0 && step.checklist.every(item => item.completed));

          return {

            id: step.id,

            status: isComplete ? 'COMPLETE' : 'PENDING',

            notes: step.notes,

            metadata: {
              checklist: step.checklist,
              partsUsed: step.partsUsed,
              attachments: step.attachments,
              collapsed: step.collapsed,
              notApplicable: step.notApplicable,
              summary: step.summary ?? null
            }
          };

        })

      };

    },

    [
      checkupSteps,
      reportGeneratedAt,
      reportIsCustom,
      reportSummary,
      selectedInterval,
      statusReason,
      taskNotes,
      taskStatus,
      totalTimeHours,
      workspaceData
    ]
  );


  const handleSave = useCallback(

    async (overrides?: { status?: MaintenanceStatus; statusReason?: string }) => {

      if (!caseId) return false;
      if (workspaceReadOnly) return false;
      const payload = buildSavePayload(overrides);
      if (!payload) return false;
      setSaving(true);

      setError(null);

      try {

        const response = await maintenanceApiForVariant.save(caseId, payload);
        const { savedAt, ...workspacePayload } = response as any;

        applyWorkspace(workspacePayload as MaintenanceWorkspacePayload, {

          savedAt: typeof savedAt === 'string' ? savedAt : null

        });
        return true;

      } catch (err) {

        console.error('Failed to save maintenance workspace', err);

        setError(err instanceof Error ? err.message : 'Failed to save maintenance workspace.');
        return false;
      } finally {

        setSaving(false);

      }

    },

    [applyWorkspace, buildSavePayload, caseId, maintenanceApiForVariant, workspaceReadOnly]
  );

  const caseSummary = workspaceData?.caseSummary ?? null;
  const headerDescription = useMemo(() => getProblemDescription(caseSummary), [caseSummary]);
  const slaHeroSummary = useMemo(() => getSlaHeroSummary(caseSummary), [caseSummary]);
  const statusLabel = useMemo(
    () =>
      taskStatus
        .split('_')
        .map(segment => segment.charAt(0) + segment.slice(1).toLowerCase())
        .join(' '),
    [taskStatus]
  );

  const statusPalette: Record<MaintenanceStatus, { background: string; color: string; border: string }> = {
    PLANNED: { background: BRAND_TEXT_SOFT, color: BRAND_TEXT, border: BRAND_TEXT_SOFT_BORDER },
    IN_PROGRESS: { background: BRAND_TEXT_SOFT, color: BRAND_TEXT, border: BRAND_TEXT_SOFT_BORDER },
    ON_HOLD: { background: BRAND_ACCENT_SOFT, color: BRAND_ACCENT_TEXT, border: BRAND_ACCENT },
    COMPLETED: { background: BRAND_SUCCESS_SOFT, color: BRAND_SUCCESS_TEXT, border: BRAND_SUCCESS_BORDER }
  };
  const statusChipStyle = {
    ...layoutStyles.statusChip,
    ...(statusPalette[taskStatus] || statusPalette.PLANNED),
    border: `1px solid ${(statusPalette[taskStatus] || statusPalette.PLANNED).border}`
  };

  const applyStatusChange = useCallback(
    async (nextStatus: MaintenanceStatus, reason: string) => {
      const trimmed = reason.trim();
      setTaskStatus(nextStatus);
      setStatusReason(trimmed);
      setHasUnsavedChanges(true);
      await handleSave({ status: nextStatus, statusReason: trimmed });
    },
    [handleSave]
  );

  const requiresStatusReason = useMemo(
    () => (status: MaintenanceStatus) =>
      (status === 'ON_HOLD' || status === 'COMPLETED') && totalSteps > 0 && completedSteps < totalSteps,
    [completedSteps, totalSteps]
  );

  const handleStatusAction = useCallback(
    (nextStatus: MaintenanceStatus) => {
      if (workspaceReadOnly) return;
      if (requiresStatusReason(nextStatus)) {
        setStatusDialog({
          mode: nextStatus,
          reason: statusReason || '',
          requireReason: true
        });
        setStatusDialogError(null);
        return;
      }
      void applyStatusChange(nextStatus, '');
    },
    [applyStatusChange, requiresStatusReason, statusReason, workspaceReadOnly]
  );

  const closeStatusDialog = useCallback(() => {
    setStatusDialog(null);
    setStatusDialogError(null);
  }, []);

  const handleStatusDialogChange = useCallback(
    (value: string) => {
      setStatusDialog(current => (current ? { ...current, reason: value } : current));
      if (statusDialogError) {
        setStatusDialogError(null);
      }
    },
    [statusDialogError]
  );

  const handleConfirmStatusDialog = useCallback(() => {
    if (!statusDialog) return;
    const trimmed = statusDialog.reason.trim();
    if (statusDialog.requireReason && !trimmed) {
      setStatusDialogError('Please provide a reason.');
      return;
    }
    void applyStatusChange(statusDialog.mode, trimmed);
    closeStatusDialog();
  }, [applyStatusChange, closeStatusDialog, statusDialog]);

  const handleTotalTimeChange = useCallback(
    (value: string) => {
      if (workspaceReadOnly) return;
      if (/^\d*\.?\d*$/.test(value)) {
        setTotalTimeHours(value);
        setHasUnsavedChanges(true);
      }
    },
    [workspaceReadOnly]
  );

  const handleNotesChange = useCallback((value: string) => {
    if (workspaceReadOnly) return;
    setTaskNotes(value);
    setHasUnsavedChanges(true);
  }, [workspaceReadOnly]);

  const askDeviceExpert = useCallback(async () => {
    if (!deviceExpertModelId) {
      setDeviceExpertError('Device model context is unavailable for this maintenance task.');
      return;
    }

    const customQuestion = deviceExpertQuestion.trim();
    const quickQuestion = selectedDeviceExpertContext
      ? `How do I carry out maintenance checklist item ${
          selectedDeviceExpertContext.number || selectedDeviceExpertContext.description
        } safely and efficiently?`
      : '';
    const technicianQuestion = customQuestion || quickQuestion;

    if (!technicianQuestion) {
      setDeviceExpertError('Select a checklist item or enter a question.');
      return;
    }

    try {
      setDeviceExpertLoading(true);
      setDeviceExpertError(null);
      const base = await getBaseUrl();
      const token = getAuthToken();
      const condensedHistory = deviceExpertMessages.slice(-6).map(message => ({
        role: message.role,
        content: message.content
      }));
      const stepContext =
        selectedDeviceExpertParent && selectedDeviceExpertContext
          ? `Maintenance step ${selectedDeviceExpertParent.sequence}: ${
              selectedDeviceExpertParent.label
            } | Checklist item ${
              selectedDeviceExpertContext.number || selectedDeviceExpertContext.label
            }: ${selectedDeviceExpertContext.description}`
          : selectedDeviceExpertParent
          ? `Maintenance step ${selectedDeviceExpertParent.sequence}: ${selectedDeviceExpertParent.label}`
          : null;
      const formattedQuestion = stepContext
        ? [`Focus step: ${stepContext}`, `Technician question: ${technicianQuestion}`].join('\n')
        : technicianQuestion;

      const response = await fetch(`${base}/api/knowledge-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          modelId: deviceExpertModelId,
          question: formattedQuestion,
          history: condensedHistory,
          conversationId: deviceExpertConversationId,
          includeFundamentals: false,
          includeImages: false,
          responseLanguage: 'EN'
        })
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to get Device Expert guidance.');
      }

      const now = new Date().toISOString();
      const assistantTimestamp = payload?.timestamp || now;
      setDeviceExpertMessages(prev => [
        ...prev,
        {
          id: `user-${now}`,
          role: 'user',
          content: technicianQuestion,
          timestamp: now
        },
        {
          id: `assistant-${assistantTimestamp}`,
          role: 'assistant',
          content: String(payload?.answer || '').trim(),
          timestamp: assistantTimestamp
        }
      ]);
      setDeviceExpertConversationId(payload?.conversationId || deviceExpertConversationId || null);
      setDeviceExpertQuestion('');
    } catch (err) {
      console.error('Maintenance task Device Expert failed', err);
      setDeviceExpertError(
        err instanceof Error ? err.message : 'Failed to get Device Expert guidance.'
      );
    } finally {
      setDeviceExpertLoading(false);
    }
  }, [
    deviceExpertConversationId,
    deviceExpertMessages,
    deviceExpertModelId,
    deviceExpertQuestion,
    selectedDeviceExpertContext,
    selectedDeviceExpertParent
  ]);

  const handleGenerateReport = useCallback(async () => {
    if (workspaceReadOnly || !caseId || reportLoading) return;
    if (taskStatus !== 'COMPLETED') return;

    try {
      if (hasUnsavedChanges) {
        const saved = await handleSave();
        if (!saved) {
          showInAppAlert('Save the maintenance task before generating the report summary.', {
            title: 'Summary generation failed',
            tone: 'danger'
          });
          return;
        }
      }

      setReportLoading(true);
      setReportEditing(false);
      setReportDraft('');
      setReportEditError(null);

      const response = await maintenanceApiForVariant.generateReport(caseId);
      const summary = typeof response?.summary === 'string' ? response.summary.trim() : '';
      if (!summary) {
        throw new Error('Summary generation returned no text.');
      }

      setReportSummary(summary);
      setReportGeneratedAt(
        typeof response?.generatedAt === 'string' ? response.generatedAt : new Date().toISOString()
      );
      setReportIsCustom(Boolean(response?.isCustom));
      setHasUnsavedChanges(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to generate maintenance report summary.';
      showInAppAlert(message, {
        title: 'Summary generation failed',
        tone: 'danger'
      });
    } finally {
      setReportLoading(false);
    }
  }, [
    caseId,
    handleSave,
    hasUnsavedChanges,
    maintenanceApiForVariant,
    reportLoading,
    taskStatus,
    workspaceReadOnly
  ]);

  const handleStartReportEdit = useCallback(() => {
    if (workspaceReadOnly || taskStatus !== 'COMPLETED' || !reportSummary) return;
    setReportDraft(reportSummary);
    setReportEditError(null);
    setReportEditing(true);
  }, [reportSummary, taskStatus, workspaceReadOnly]);

  const handleCancelReportEdit = useCallback(() => {
    setReportDraft('');
    setReportEditError(null);
    setReportEditing(false);
  }, []);

  const handleSaveReportEdit = useCallback(async () => {
    if (workspaceReadOnly || !caseId || reportLoading || taskStatus !== 'COMPLETED') return;
    const trimmed = reportDraft.trim();
    if (!trimmed) {
      setReportEditError('Enter summary text before saving.');
      return;
    }

    try {
      setReportLoading(true);
      setReportEditError(null);
      const response = await maintenanceApiForVariant.saveReport(caseId, trimmed);
      setReportSummary(typeof response?.summary === 'string' ? response.summary : trimmed);
      setReportGeneratedAt(
        typeof response?.generatedAt === 'string' ? response.generatedAt : new Date().toISOString()
      );
      setReportIsCustom(Boolean(response?.isCustom ?? true));
      setReportDraft('');
      setReportEditing(false);
      setHasUnsavedChanges(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save maintenance summary.';
      setReportEditError(message);
      showInAppAlert(message, {
        title: 'Summary save failed',
        tone: 'danger'
      });
    } finally {
      setReportLoading(false);
    }
  }, [caseId, maintenanceApiForVariant, reportDraft, reportLoading, taskStatus, workspaceReadOnly]);

  const closeServiceReportModal = useCallback(() => {
    if (serviceReportSaving || serviceReportLoading || serviceReportSending) return;
    setServiceReportModalOpen(false);
    setServiceReportDraft(null);
    setServiceReportError(null);
    setServiceReportMode('create');
  }, [serviceReportLoading, serviceReportSaving, serviceReportSending]);

  const handleViewServiceReport = useCallback(() => {
    const document = workspaceData?.serviceReport?.document || null;
    if (!document) return;
    setServiceReportDraft(document);
    setServiceReportMode('view');
    setServiceReportError(null);
    setServiceReportModalOpen(true);
  }, [workspaceData?.serviceReport?.document]);

  const handleCreateServiceReport = useCallback(async () => {
    if (workspaceReadOnly || !caseId || serviceReportLoading || serviceReportSaving || serviceReportSending) return;
    if (taskStatus !== 'COMPLETED') {
      setServiceReportError('Complete the maintenance task before creating a service report.');
      return;
    }
    if (savedServiceReportSent) {
      setServiceReportError('This service report has already been sent and cannot be regenerated.');
      return;
    }
    if (!reportSummary?.trim()) {
      setServiceReportError('Generate and review the maintenance summary before creating a service report.');
      return;
    }

    try {
      if (hasUnsavedChanges) {
        const saved = await handleSave();
        if (!saved) {
          throw new Error('Save the maintenance task before creating a service report.');
        }
      }

      setServiceReportLoading(true);
      setServiceReportError(null);
      const response = (await maintenanceApiForVariant.createServiceReportDraft(caseId)) as {
        document?: ServiceReportDocument | null;
        reportSummary?: string | null;
        existingReport?: ServiceReportRecord | null;
        template?: ReportTemplate | null;
      };
      const document = response?.document || null;
      if (!document) {
        throw new Error('Service report template did not return a document preview.');
      }

      const generatedSummary =
        typeof response?.reportSummary === 'string' ? response.reportSummary.trim() : '';
      if (generatedSummary) {
        setReportSummary(generatedSummary);
      }

      setWorkspaceData(previous =>
        previous
          ? {
              ...previous,
              serviceReport: response?.existingReport || previous.serviceReport || null,
              serviceReportTemplate: response?.template || previous.serviceReportTemplate || null
            }
          : previous
      );
      setServiceReportDraft(document);
      setServiceReportMode('create');
      setServiceReportModalOpen(true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create maintenance service report.';
      setServiceReportError(message);
      showInAppAlert(message, {
        title: 'Service report failed',
        tone: 'danger'
      });
    } finally {
      setServiceReportLoading(false);
    }
  }, [
    caseId,
    handleSave,
    hasUnsavedChanges,
    maintenanceApiForVariant,
    reportSummary,
    savedServiceReportSent,
    serviceReportLoading,
    serviceReportSaving,
    serviceReportSending,
    taskStatus,
    workspaceReadOnly
  ]);

  const handleSaveServiceReport = useCallback(async () => {
    if (workspaceReadOnly || !caseId || serviceReportSaving || serviceReportSending || !serviceReportDraft) return;

    try {
      setServiceReportSaving(true);
      setServiceReportError(null);
      const response = (await maintenanceApiForVariant.saveServiceReport(
        caseId,
        serviceReportDraft as unknown as Record<string, unknown>
      )) as {
        serviceReport?: ServiceReportRecord | null;
      };
      const savedReport = response?.serviceReport || null;
      if (!savedReport?.document) {
        throw new Error('Service report save did not return a saved report snapshot.');
      }

      setWorkspaceData(previous =>
        previous
          ? {
              ...previous,
              serviceReport: savedReport
            }
          : previous
      );
      setServiceReportDraft(savedReport.document);
      setServiceReportMode('view');
      setLastSavedAt(savedReport.savedAt || new Date().toISOString());
      setHasUnsavedChanges(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save maintenance service report.';
      setServiceReportError(message);
    } finally {
      setServiceReportSaving(false);
    }
  }, [
    caseId,
    maintenanceApiForVariant,
    serviceReportDraft,
    serviceReportSaving,
    serviceReportSending,
    workspaceReadOnly
  ]);

  const handleSendServiceReport = useCallback(async () => {
    if (workspaceReadOnly || !caseId || serviceReportSending) return;
    const reportId = workspaceData?.serviceReport?.id || '';
    if (!reportId) {
      const message = 'Save the service report before sending it.';
      setServiceReportError(message);
      showInAppAlert(message, { title: 'Service report send failed', tone: 'danger' });
      return;
    }

    try {
      setServiceReportSending(true);
      setServiceReportError(null);
      const response = (await maintenanceApiForVariant.sendServiceReport(caseId, reportId)) as {
        serviceReport?: ServiceReportRecord | null;
      };
      const sentReport = response?.serviceReport || null;
      if (!sentReport?.document) {
        throw new Error('Service report send did not return a saved report snapshot.');
      }
      setWorkspaceData(prev => (prev ? { ...prev, serviceReport: sentReport } : prev));
      setServiceReportDraft(sentReport.document);
      setServiceReportMode('view');
      await promptCaseCloseAfterServiceReport(caseId, {
        triggerTaskId: workspaceData?.task?.id || null
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send service report.';
      setServiceReportError(message);
      showInAppAlert(message, { title: 'Service report send failed', tone: 'danger' });
    } finally {
      setServiceReportSending(false);
    }
  }, [
    caseId,
    maintenanceApiForVariant,
    serviceReportSending,
    workspaceData?.serviceReport?.id,
    workspaceData?.task?.id,
    workspaceReadOnly
  ]);

  const toggleStepCollapsed = useCallback((stepId: string) => {
    if (workspaceReadOnly) return;
    setCheckupSteps(prev =>
      prev.map(step => (step.id === stepId ? { ...step, collapsed: !step.collapsed } : step))
    );
    setHasUnsavedChanges(true);
  }, [workspaceReadOnly]);

  const toggleChecklistItem = useCallback(
    (stepId: string, itemId: string) => {
      if (workspaceReadOnly) return;
      setCheckupSteps(prev =>
        prev.map(step => {
          if (step.id !== stepId) return step;
          const checklist = step.checklist.map(item =>
            item.id === itemId ? { ...item, completed: !item.completed } : item
          );
          return { ...step, checklist };
        })
      );
      setHasUnsavedChanges(true);
      if (taskStatus === 'PLANNED') {
        setTaskStatus('IN_PROGRESS');
      }
    },
    [taskStatus, workspaceReadOnly]
  );

  const handleChecklistCaptureValueChange = useCallback((stepId: string, itemId: string, value: string) => {
    if (workspaceReadOnly) return;
    setCheckupSteps(prev =>
      prev.map(step => {
        if (step.id !== stepId) return step;
        return {
          ...step,
          checklist: step.checklist.map(item =>
            item.id === itemId ? { ...item, captureValue: value } : item
          )
        };
      })
    );
    setHasUnsavedChanges(true);
  }, [workspaceReadOnly]);

  const toggleCaptureDetails = useCallback((stepId: string, itemId: string) => {
    const key = `${stepId}:${itemId}`;
    setCaptureDetailsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const openPreviewImages = useCallback((images: ReferenceImageHit[] = []) => {
    const gallery = dedupeReferenceImages(images).filter(image => image.fileUrl);
    if (gallery.length === 0) return;
    setPreviewImages(gallery);
    setPreviewImageIndex(0);
    setPreviewZoom(100);
  }, []);

  const closePreviewImages = useCallback(() => {
    setPreviewImages([]);
    setPreviewImageIndex(0);
    setPreviewZoom(100);
  }, []);

  const showPreviousPreviewImage = useCallback(() => {
    setPreviewImageIndex(index => Math.max(0, index - 1));
    setPreviewZoom(100);
  }, []);

  const showNextPreviewImage = useCallback(() => {
    setPreviewImageIndex(index => Math.min(previewImages.length - 1, index + 1));
    setPreviewZoom(100);
  }, [previewImages.length]);

  const selectPreviewImage = useCallback((index: number) => {
    setPreviewImageIndex(Math.max(0, Math.min(previewImages.length - 1, index)));
    setPreviewZoom(100);
  }, [previewImages.length]);

  const renderReferenceImageTextLinks = useCallback(
    (step: CheckupStep, item: CheckupChecklistItem) => {
      const itemText = composeChecklistItemText(item);
      const labelText = normalizeWhitespace(String(item.label || ''));
      const isMeasurementItem = String(item.capture?.type || '').toUpperCase() === 'MEASUREMENT';
      const specText = isMeasurementItem ? '' : normalizeWhitespace(String(item.spec || ''));
      const fallbackContent = (
        <>
          <span style={checklistStyles.checklistPrimaryText}>{labelText}</span>
          {specText && (
            <span style={checklistStyles.checklistDetailText}> | {specText}</span>
          )}
        </>
      );
      if (!itemText) return { content: fallbackContent, hasLinks: false };

      const stepReferenceImages = dedupeReferenceImages(
        Array.isArray(step.referenceImages) ? step.referenceImages : []
      );
      const blocks = Array.isArray(step.referenceImageBlocks) ? step.referenceImageBlocks : [];
      const block = blocks.find(candidate => {
        const candidateItemId = String((candidate as { itemId?: string })?.itemId || '').trim();
        if (candidateItemId && item.id && candidateItemId === String(item.id)) return true;
        const candidateText = normalizeWhitespace(String(candidate?.text || ''));
        return candidateText && candidateText === itemText;
      });

      if (!block || !Array.isArray(block.imageIds) || block.imageIds.length === 0) {
        return { content: fallbackContent, hasLinks: false };
      }

      const imageById = new Map(stepReferenceImages.map(image => [String(image.id), image]));
      const blockImageIds = block.imageIds.map(String);
      const anchors = Array.isArray(block.anchors) ? block.anchors : [];

      const linkText = (text: string, textKey: string) => {
        const occupiedRanges: Array<{ start: number; end: number }> = [];
        const renderableAnchors = anchors
          .map((anchor, anchorIndex) => {
          const anchorText = String(anchor?.text || '').trim();
          if (!anchorText) return null;
          const anchorImageIds =
            Array.isArray(anchor?.imageIds) && anchor.imageIds.length > 0
              ? anchor.imageIds.map(String)
              : blockImageIds;
          const gallery = dedupeReferenceImages(
            anchorImageIds
              .map(imageId => imageById.get(imageId))
              .filter((image): image is ReferenceImageHit => Boolean(image?.id))
          );
          if (gallery.length === 0) return null;
          const range = findNonOverlappingPhraseRange(text, anchorText, occupiedRanges);
          if (!range) return null;
          occupiedRanges.push(range);
          return {
            key: `${textKey}:${anchorText}:${anchorIndex}:${range.start}`,
            start: range.start,
            end: range.end,
            gallery
          };
        })
        .filter(
          (
            anchor
          ): anchor is {
            key: string;
            start: number;
            end: number;
            gallery: ReferenceImageHit[];
          } => Boolean(anchor)
        )
        .sort((left, right) => left.start - right.start);

        if (renderableAnchors.length === 0) return { content: text, hasLinks: false };

        const parts: React.ReactNode[] = [];
        let cursor = 0;
        renderableAnchors.forEach(anchor => {
          if (anchor.start > cursor) parts.push(text.slice(cursor, anchor.start));
          parts.push(
            <button
              type="button"
              key={`maintenance-image-anchor:${step.id}:${item.id}:${anchor.key}`}
              style={checklistStyles.inlineImageTextLink}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                openPreviewImages(anchor.gallery);
              }}
            >
              {text.slice(anchor.start, anchor.end)}
            </button>
          );
          cursor = anchor.end;
        });
        if (cursor < text.length) parts.push(text.slice(cursor));
        return { content: parts, hasLinks: true };
      };

      const linkedLabel = linkText(labelText, 'label');
      const linkedSpec = specText ? linkText(specText, 'spec') : { content: null, hasLinks: false };

      return {
        content: (
          <>
            <span style={checklistStyles.checklistPrimaryText}>{linkedLabel.content}</span>
            {specText && (
              <span style={checklistStyles.checklistDetailText}> | {linkedSpec.content}</span>
            )}
          </>
        ),
        hasLinks: linkedLabel.hasLinks || linkedSpec.hasLinks
      };
    },
    [openPreviewImages]
  );

  const renderMeasurementCapture = useCallback(
    (stepId: string, item: CheckupChecklistItem) => {
      const capture = item.capture;
      if (!capture || String(capture.type || '').toUpperCase() !== 'MEASUREMENT') return null;
      const captureValue = String(item.captureValue || '');
      const hint = formatMeasurementHint(capture);
      const specDetail = normalizeWhitespace(String(item.spec || ''));
      const detailParts = [
        specDetail,
        hint && normalizeWhitespace(hint) !== specDetail ? hint : null
      ].filter((value): value is string => Boolean(value));
      const detailKey = `${stepId}:${item.id}`;
      const detailsOpen = captureDetailsOpen[detailKey] === true;
      let inputStyle: React.CSSProperties = { ...checklistStyles.captureInput };
      if (workspaceReadOnly) {
        inputStyle = { ...inputStyle, background: BRAND_SURFACE, cursor: 'not-allowed' };
      }

      return (
        <div style={checklistStyles.captureRow}>
          <div style={checklistStyles.captureInputGroup}>
            <div style={checklistStyles.captureLabelRow}>
              <label style={checklistStyles.captureLabel}>Measured value</label>
              {detailParts.length > 0 && (
                <button
                  type="button"
                  style={checklistStyles.captureDetailsToggle}
                  onClick={() => toggleCaptureDetails(stepId, item.id)}
                >
                  {detailsOpen ? 'Hide details' : 'Show details'}
                </button>
              )}
            </div>
            <input
              style={inputStyle}
              type="text"
              value={captureValue}
              placeholder={formatMeasurementPlaceholder(capture)}
              onChange={event =>
                handleChecklistCaptureValueChange(stepId, item.id, event.target.value)
              }
              disabled={workspaceReadOnly}
            />
          </div>
          {detailsOpen && detailParts.length > 0 && (
            <div style={checklistStyles.captureHint}>{detailParts.join(' | ')}</div>
          )}
        </div>
      );
    },
    [captureDetailsOpen, handleChecklistCaptureValueChange, toggleCaptureDetails, workspaceReadOnly]
  );

  const handleStepNoteChange = useCallback((stepId: string, value: string) => {
    if (workspaceReadOnly) return;
    setCheckupSteps(prev =>
      prev.map(step => (step.id === stepId ? { ...step, notes: value } : step))
    );
    setHasUnsavedChanges(true);
  }, [workspaceReadOnly]);

  const handleToggleNotApplicable = useCallback((stepId: string, value: boolean) => {
    if (workspaceReadOnly) return;
    setCheckupSteps(prev =>
      prev.map(step => (step.id === stepId ? { ...step, notApplicable: value } : step))
    );
    setHasUnsavedChanges(true);
  }, [workspaceReadOnly]);

  const handleRemoveAttachment = useCallback((stepId: string, attachmentId: string) => {
    if (workspaceReadOnly) return;
    setCheckupSteps(prev =>
      prev.map(step => {
        if (step.id !== stepId) return step;
        const attachments = step.attachments.filter(attachment => attachment.id !== attachmentId);
        return { ...step, attachments };
      })
    );
    setHasUnsavedChanges(true);
  }, [workspaceReadOnly]);

  const handlePartDraftFieldChange = useCallback(
    (stepId: string, field: keyof Omit<PartDraft, 'quantity'>, value: string) => {
      if (workspaceReadOnly) return;
      setPartDrafts(prev => ({
        ...prev,
        [stepId]: {
          ...(prev[stepId] || createEmptyPartDraft()),
          [field]: value
        }
      }));
      setPartScanStates(prev => ({
        ...prev,
        [stepId]: prev[stepId] === 'error' ? 'idle' : prev[stepId] || 'idle'
      }));
      setPartScanMessages(prev => ({
        ...prev,
        [stepId]: prev[stepId] && prev[stepId]?.toLowerCase().includes('enter quantity') ? null : prev[stepId] || null
      }));
    },
    [workspaceReadOnly]
  );

  const handlePartDraftQuantityChange = useCallback(
    (stepId: string, value: string) => {
      if (workspaceReadOnly) return;
      if (!/^\d*\.?\d*$/.test(value)) return;
      setPartDrafts(prev => ({
        ...prev,
        [stepId]: {
          ...(prev[stepId] || createEmptyPartDraft()),
          quantity: value
        }
      }));
      setPartScanStates(prev => ({
        ...prev,
        [stepId]: prev[stepId] === 'error' ? 'idle' : prev[stepId] || 'idle'
      }));
      setPartScanMessages(prev => ({
        ...prev,
        [stepId]: prev[stepId] && prev[stepId]?.toLowerCase().includes('enter quantity') ? null : prev[stepId] || null
      }));
    },
    [workspaceReadOnly]
  );

  const handleAddPart = useCallback(
    (stepId: string) => {
      if (workspaceReadOnly) return;
      const draft = partDrafts[stepId] || createEmptyPartDraft();
      const hasIdentity = Boolean(draft.partNumber.trim() || draft.description.trim());
      if (!hasIdentity) {
        setPartScanStates(prev => ({ ...prev, [stepId]: 'error' }));
        setPartScanMessages(prev => ({
          ...prev,
          [stepId]: 'Enter a part number or description before adding a replacement part.'
        }));
        return;
      }
      const quantityValue = normalizePartQuantity(draft.quantity);
      if (quantityValue === null) {
        setPartScanStates(prev => ({ ...prev, [stepId]: 'error' }));
        setPartScanMessages(prev => ({
          ...prev,
          [stepId]: 'Enter a quantity before adding the replacement part.'
        }));
        return;
      }
      const nextPart: PartUsage = {
        id: createPartUsageId(),
        partNumber: draft.partNumber.trim(),
        description: draft.description.trim(),
        batchNumber: draft.batchNumber.trim(),
        quantity: quantityValue
      };
      setCheckupSteps(prev =>
        prev.map(step =>
          step.id === stepId
            ? { ...step, partsUsed: [...step.partsUsed, nextPart] }
            : step
        )
      );
      setPartDrafts(prev => ({ ...prev, [stepId]: createEmptyPartDraft() }));
      setPartScanStates(prev => ({ ...prev, [stepId]: 'idle' }));
      setPartScanMessages(prev => ({ ...prev, [stepId]: null }));
      setHasUnsavedChanges(true);
      if (taskStatus === 'PLANNED') {
        setTaskStatus('IN_PROGRESS');
      }
    },
    [partDrafts, taskStatus, workspaceReadOnly]
  );

  const handleRemovePart = useCallback(
    (stepId: string, partId: string) => {
      if (workspaceReadOnly) return;
      setCheckupSteps(prev =>
        prev.map(step =>
          step.id === stepId
            ? { ...step, partsUsed: step.partsUsed.filter(part => part.id !== partId) }
            : step
        )
      );
      setHasUnsavedChanges(true);
    },
    [workspaceReadOnly]
  );

  const handleSelectReferenceKitPart = useCallback(
    (stepId: string) => {
      if (workspaceReadOnly || !referenceKitPart) return;
      setPartDrafts(prev => ({
        ...prev,
        [stepId]: {
          ...(prev[stepId] || createEmptyPartDraft()),
          partNumber: referenceKitPart.partNumber,
          description: referenceKitPart.description
        }
      }));
    },
    [referenceKitPart, workspaceReadOnly]
  );

  const handlePartScanClick = useCallback(
    (stepId: string) => {
      if (workspaceReadOnly) return;
      if ((partScanStates[stepId] || 'idle') === 'pending') return;
      partScanInputRefs.current[stepId]?.click();
    },
    [partScanStates, workspaceReadOnly]
  );

  const handlePartScanInputChange = useCallback(
    async (stepId: string, event: React.ChangeEvent<HTMLInputElement>) => {
      if (workspaceReadOnly) return;
      const file = event.target.files?.[0] ?? null;
      event.target.value = '';
      if (!file) return;
      setPartScanStates(prev => ({ ...prev, [stepId]: 'pending' }));
      setPartScanMessages(prev => ({ ...prev, [stepId]: null }));
      try {
        const response = await repairApi.scanPartLabel(file);
        if (!response || response.success === false || !response.found || !response.part) {
          setPartScanStates(prev => ({ ...prev, [stepId]: 'error' }));
          setPartScanMessages(prev => ({
            ...prev,
            [stepId]: 'No part details detected. Enter the replacement part manually or try another photo.'
          }));
          return;
        }
        const part = response.part as Record<string, unknown>;
        setPartDrafts(prev => ({
          ...prev,
          [stepId]: {
            partNumber: typeof part.partNumber === 'string' ? part.partNumber : '',
            description: typeof part.description === 'string' ? part.description : '',
            batchNumber: typeof part.batchNumber === 'string' ? part.batchNumber : '',
            quantity: ''
          }
        }));
        const detailBits: string[] = [];
        if (typeof part.manufacturedDate === 'string' && part.manufacturedDate.trim()) {
          detailBits.push(`Manufactured ${part.manufacturedDate.trim()}`);
        }
        if (typeof part.notes === 'string' && part.notes.trim()) {
          detailBits.push(part.notes.trim());
        }
        detailBits.push('Enter quantity before adding.');
        setPartScanStates(prev => ({ ...prev, [stepId]: 'success' }));
        setPartScanMessages(prev => ({
          ...prev,
          [stepId]: detailBits.join(' | ')
        }));
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to scan part label.';
        setPartScanStates(prev => ({ ...prev, [stepId]: 'error' }));
        setPartScanMessages(prev => ({ ...prev, [stepId]: message }));
      }
    },
    [workspaceReadOnly]
  );

  const handleAttachmentInputChange = useCallback(
    async (stepId: string, event: React.ChangeEvent<HTMLInputElement>) => {
      if (workspaceReadOnly) return;
      const file = event.target.files?.[0] ?? null;
      event.target.value = '';
      if (!file) return;
      if (file.size > MAX_ATTACHMENT_BYTES) {
        window.alert('File is too large. Please choose a file under 6 MB.');
        return;
      }
      let dataUrl: string;
      try {
        dataUrl = await readFileAsDataUrl(file);
      } catch (error) {
        window.alert('Unable to read file. Please try again.');
        return;
      }
      const attachment: StepAttachment = {
        id: createStepAttachmentId(),
        name: file.name,
        size: file.size,
        dataUrl,
        addedAt: new Date().toISOString()
      };
      setCheckupSteps(prev =>
        prev.map(step => {
          if (step.id !== stepId) return step;
          if (step.attachments.length >= MAX_STEP_ATTACHMENTS) {
            window.alert('Maximum number of attachments reached for this step.');
            return step;
          }
          return { ...step, attachments: [...step.attachments, attachment] };
        })
      );
      setHasUnsavedChanges(true);
    },
    [workspaceReadOnly]
  );

  const handleFlagStep = useCallback((stepLabel: string) => {
    window.alert(`Flagging issues for "${stepLabel}" coming soon.`);
  }, []);

  return (
    <div style={layoutStyles.page}>
      <div style={layoutStyles.topBar}>
        <button style={layoutStyles.backLink} onClick={() => navigate('/tech')}>
          Back to Cases
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={layoutStyles.saveMeta}>
            {saving
              ? 'Saving...'
              : hasUnsavedChanges
              ? 'Unsaved changes'
              : lastSavedAt
              ? `Last saved ${formatDateTime(lastSavedAt)}`
              : 'Not saved yet'}
          </div>
          <button
            style={{
              ...layoutStyles.buttonPrimary,
              opacity: saving || !hasUnsavedChanges || workspaceReadOnly ? 0.6 : 1,
              cursor: saving || !hasUnsavedChanges || workspaceReadOnly ? 'not-allowed' : 'pointer'
            }}
            disabled={saving || !hasUnsavedChanges || workspaceReadOnly}
            onClick={() => handleSave()}
          >
            {saving ? 'Saving...' : variant === 'preventative' ? 'Save maintenance' : 'Save check up'}
          </button>
        </div>
      </div>

      {workspaceReadOnly && (
        <div
          style={{
            background: BRAND_TEXT_SOFT,
            color: BRAND_TEXT,
            border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
            borderRadius: '0.9rem',
            padding: '0.9rem 1.2rem',
            fontWeight: 600
          }}
        >
          Read-only view. Only the assigned technician can edit this task
          {assignedLabel ? ` (assigned to ${assignedLabel}).` : '.'}
        </div>
      )}

      {error && (
        <div
          style={{
            background: BRAND_DANGER_SOFT,
            color: BRAND_DANGER_TEXT,
            border: `1px solid ${BRAND_DANGER_BORDER}`,
            borderRadius: '0.9rem',
            padding: '0.9rem 1.2rem',
            fontWeight: 600
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ ...layoutStyles.card, alignItems: 'center' }}>
          {loadingMessage}
        </div>
      ) : !caseSummary ? (
        <div style={{ ...layoutStyles.card, alignItems: 'center' }}>No case data available.</div>
      ) : (
        <>
          <div style={layoutStyles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: BRAND_TEXT }}>
                  {taskTitle}
                </div>
                <div style={{ fontWeight: 600, color: BRAND_TEXT, maxWidth: '52rem', lineHeight: 1.4 }}>
                  {headerDescription}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  style={{
                    ...layoutStyles.buttonPrimary,
                    opacity: refreshing || workspaceReadOnly ? 0.75 : 1
                  }}
                  onClick={handleRefresh}
                  disabled={refreshing || workspaceReadOnly}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh from defaults'}
                </button>
                <button
                  style={{
                    ...layoutStyles.buttonSecondary,
                    opacity: clearing || workspaceReadOnly ? 0.75 : 1
                  }}
                  onClick={handleClearProgress}
                  disabled={clearing || workspaceReadOnly}
                >
                  {clearing ? 'Clearing...' : 'Clear local progress'}
                </button>
              </div>
            </div>
            {availableIntervals.length > 0 && (
              <div style={layoutStyles.intervalPickerRow}>
                <div style={layoutStyles.intervalPickerLabel}>Check up interval</div>
                <select
                  style={layoutStyles.intervalSelect}
                  value={selectedInterval}
                  onChange={event => handleIntervalChange(event.target.value)}
                  disabled={referenceLoading || workspaceReadOnly}
                >
                  {availableIntervals.map(interval => (
                    <option key={interval} value={interval}>
                      {interval}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={layoutStyles.grid}>
              <div>
                <div style={layoutStyles.fieldLabel}>Case</div>
                <div style={layoutStyles.fieldValue}>{getCaseNumber(caseSummary)}</div>
              </div>
              <div>
                <div style={layoutStyles.fieldLabel}>Customer</div>
                <div style={layoutStyles.fieldValue}>{getCustomerName(caseSummary)}</div>
              </div>
              <div>
                <div style={layoutStyles.fieldLabel}>Device</div>
                <div style={layoutStyles.fieldValue}>{getDeviceLabel(caseSummary)}</div>
              </div>
              <div>
                <div style={layoutStyles.fieldLabel}>Location</div>
                <div style={layoutStyles.fieldValue}>{getLocationName(caseSummary)}</div>
              </div>
              <div>
                <div style={layoutStyles.fieldLabel}>Contact</div>
                <div style={layoutStyles.fieldValue}>{getContactName(caseSummary)}</div>
              </div>
              <div>
                <div style={layoutStyles.fieldLabel}>SLA</div>
                <div style={layoutStyles.fieldValue}>{slaHeroSummary.value}</div>
                <div style={layoutStyles.statusNote}>{slaHeroSummary.note}</div>
              </div>
            </div>
          </div>

          <div style={layoutStyles.section}>
            <div style={layoutStyles.statusHeader}>
              <div style={layoutStyles.statusLeft}>
                <div>
                  <div style={layoutStyles.fieldLabel}>Status</div>
                  <div style={layoutStyles.statusChipRow}>
                    <span style={statusChipStyle}>{statusLabel}</span>
                  </div>
                  {statusReason && (
                    <div style={layoutStyles.statusNote}>Status note: {statusReason}</div>
                  )}
                </div>
                <div style={layoutStyles.statusMetaRow}>
                  <div style={layoutStyles.statusMetaItem}>
                    <div style={layoutStyles.fieldLabel}>Checklist progress</div>
                    <div style={layoutStyles.fieldValue}>
                      {totalSteps > 0 ? `${completedSteps} of ${totalSteps}` : '0 recorded'}
                    </div>
                  </div>
                  <div style={{ ...layoutStyles.statusMetaItem, maxWidth: '12rem' }}>
                    <div style={layoutStyles.fieldLabel}>Total time (hours)</div>
                    <input
                      type="text"
                      placeholder="e.g. 2.5"
                      value={totalTimeHours}
                      onChange={event => handleTotalTimeChange(event.target.value)}
                      style={{
                        ...layoutStyles.textInput,
                        ...(taskStatus === 'COMPLETED' || workspaceReadOnly
                          ? layoutStyles.textInputDisabled
                          : {})
                      }}
                      disabled={taskStatus === 'COMPLETED' || workspaceReadOnly}
                    />
                  </div>
                </div>
              </div>
              <div style={layoutStyles.statusRight}>
                <div style={layoutStyles.statusRightLabel}>Actions</div>
                <div style={layoutStyles.primaryActions}>
                  <button
                    type="button"
                    style={{
                      ...layoutStyles.statusActionButton,
                      ...layoutStyles.statusActionComplete,
                      ...(taskStatus === 'COMPLETED' || workspaceReadOnly
                        ? layoutStyles.statusActionDisabled
                        : {})
                    }}
                    onClick={() => handleStatusAction('COMPLETED')}
                    disabled={taskStatus === 'COMPLETED' || workspaceReadOnly}
                  >
                    Mark complete
                  </button>
                  <button
                    type="button"
                    style={{
                      ...layoutStyles.statusActionButton,
                      ...layoutStyles.statusActionHold,
                      ...(taskStatus === 'ON_HOLD' || workspaceReadOnly
                        ? layoutStyles.statusActionDisabled
                        : {})
                    }}
                    onClick={() => handleStatusAction('ON_HOLD')}
                    disabled={taskStatus === 'ON_HOLD' || workspaceReadOnly}
                  >
                    Place on hold
                  </button>
                  <button
                    type="button"
                    style={{
                      ...layoutStyles.statusActionButton,
                      ...layoutStyles.statusActionReopen,
                      ...(taskStatus === 'IN_PROGRESS' || workspaceReadOnly
                        ? layoutStyles.statusActionDisabled
                        : {})
                    }}
                    onClick={() => handleStatusAction('IN_PROGRESS')}
                    disabled={taskStatus === 'IN_PROGRESS' || workspaceReadOnly}
                  >
                    Re-open
                  </button>
                </div>
                <button
                  type="button"
                  style={{
                    ...layoutStyles.reportButton,
                    ...(taskStatus !== 'COMPLETED' || workspaceReadOnly || reportLoading
                      ? layoutStyles.reportButtonDisabled
                      : {})
                  }}
                  onClick={handleGenerateReport}
                  disabled={taskStatus !== 'COMPLETED' || workspaceReadOnly || reportLoading}
                >
                  {reportLoading ? 'Generating...' : reportSummary ? 'Regenerate summary' : 'Generate summary'}
                </button>
              </div>
            </div>

            <div>
              <div style={layoutStyles.fieldLabel}>Task notes</div>
              <textarea
                style={layoutStyles.textarea}
                placeholder="Overall notes, risks, follow-ups..."
                value={taskNotes}
                onChange={event => handleNotesChange(event.target.value)}
                disabled={workspaceReadOnly}
              />
            </div>
          </div>

          {(reportSummary || reportEditing) && (
            <div style={layoutStyles.reportCard}>
              <div style={layoutStyles.reportHeader}>
                <div>
                  <div style={layoutStyles.reportTitle}>Customer summary</div>
                  <div style={layoutStyles.reportMeta}>
                    {reportGeneratedAt
                      ? `${reportIsCustom ? 'Last updated' : 'Generated'} ${formatDateTime(reportGeneratedAt)}`
                      : 'Generated summary'}
                  </div>
                  {workspaceData?.serviceReport?.savedAt && (
                    <div style={layoutStyles.reportMeta}>
                      Service report saved {formatDateTime(workspaceData.serviceReport.savedAt)}
                    </div>
                  )}
                  {serviceReportError && (
                    <div style={layoutStyles.reportError}>{serviceReportError}</div>
                  )}
                </div>
                <div style={layoutStyles.reportActions}>
                  {!reportEditing && reportSummary && !workspaceReadOnly && (
                    <button
                      type="button"
                      style={layoutStyles.reportSecondaryButton}
                      onClick={handleCreateServiceReport}
                      disabled={
                        serviceReportLoading ||
                        serviceReportSaving ||
                        serviceReportSending ||
                        reportLoading ||
                        savedServiceReportSent ||
                        taskStatus !== 'COMPLETED'
                      }
                    >
                      {serviceReportLoading
                        ? 'Building service report...'
                        : workspaceData?.serviceReport?.document
                        ? 'Regenerate service report'
                        : 'Create service report'}
                    </button>
                  )}
                  {!reportEditing && workspaceData?.serviceReport?.document && (
                    <button
                      type="button"
                      style={layoutStyles.reportSecondaryButton}
                      onClick={handleViewServiceReport}
                      disabled={serviceReportLoading || serviceReportSaving || serviceReportSending}
                    >
                      View service report
                    </button>
                  )}
                  {!reportEditing && reportSummary && !workspaceReadOnly && (
                    <button
                      type="button"
                      style={layoutStyles.reportSecondaryButton}
                      onClick={handleStartReportEdit}
                      disabled={reportLoading || taskStatus !== 'COMPLETED'}
                    >
                      Edit summary
                    </button>
                  )}
                </div>
              </div>
              {reportEditing ? (
                <>
                  <textarea
                    style={layoutStyles.reportTextarea}
                    value={reportDraft}
                    onChange={event => {
                      setReportDraft(event.target.value);
                      setReportEditError(null);
                    }}
                    disabled={reportLoading || workspaceReadOnly}
                  />
                  {reportEditError && <div style={layoutStyles.reportError}>{reportEditError}</div>}
                  <div style={layoutStyles.reportEditActions}>
                    <button
                      type="button"
                      style={layoutStyles.reportSecondaryButton}
                      onClick={handleCancelReportEdit}
                      disabled={reportLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      style={layoutStyles.reportPrimaryButton}
                      onClick={handleSaveReportEdit}
                      disabled={reportLoading || workspaceReadOnly}
                    >
                      {reportLoading ? 'Saving...' : 'Save summary'}
                    </button>
                  </div>
                </>
              ) : (
                <div style={layoutStyles.reportBody}>{reportSummary}</div>
              )}
            </div>
          )}

          <div style={layoutStyles.section}>
            <div style={layoutStyles.expertSection}>
              <div style={layoutStyles.expertHeader}>
                <div style={layoutStyles.expertHeaderCopy}>
                  <div style={layoutStyles.expertTitle}>Device Expert</div>
                  <div style={layoutStyles.expertSub}>
                    Ask about a maintenance step for contextual help, or ask an open device question.
                  </div>
                </div>
                <button
                  type="button"
                  style={layoutStyles.buttonSecondary}
                  onClick={() => setDeviceExpertOpen(prev => !prev)}
                >
                  {deviceExpertOpen ? 'Hide' : 'Show'}
                </button>
              </div>
              {deviceExpertOpen && (
                <>
                  <div style={layoutStyles.expertGrid}>
                    <div style={layoutStyles.expertField}>
                      <label style={layoutStyles.expertLabel}>Maintenance step</label>
                      <select
                        style={layoutStyles.expertSelect}
                        value={deviceExpertStepId}
                        onChange={event => {
                          setDeviceExpertStepId(event.target.value);
                          setDeviceExpertContextKey('');
                          setDeviceExpertError(null);
                        }}
                        disabled={deviceExpertLoading || deviceExpertParentOptions.length === 0}
                      >
                        <option value="">No specific maintenance step selected</option>
                        {deviceExpertParentOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.sequence}. {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={layoutStyles.expertField}>
                      <label style={layoutStyles.expertLabel}>Checklist item</label>
                      <select
                        style={layoutStyles.expertSelect}
                        value={deviceExpertContextKey}
                        onChange={event => {
                          setDeviceExpertContextKey(event.target.value);
                          setDeviceExpertError(null);
                        }}
                        disabled={deviceExpertLoading || !selectedDeviceExpertParent}
                      >
                        <option value="">
                          {selectedDeviceExpertParent
                            ? 'No checklist item selected'
                            : 'Select a maintenance step first'}
                        </option>
                        {deviceExpertContextOptions.map(option => (
                          <option key={option.key} value={option.key}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={layoutStyles.expertField}>
                    <label style={layoutStyles.expertLabel}>Question</label>
                    <input
                      style={layoutStyles.expertInput}
                      value={deviceExpertQuestion}
                      onChange={event => {
                        setDeviceExpertQuestion(event.target.value);
                        setDeviceExpertError(null);
                      }}
                      placeholder="Ask a maintenance question, or leave blank to get more detail on the selected checklist item."
                      disabled={deviceExpertLoading || !deviceExpertModelId}
                    />
                    <div style={layoutStyles.expertHint}>
                      {deviceExpertModelId
                        ? 'Choosing a checklist item adds that task context. With no step selected, your question is treated as an open Device Expert ask.'
                        : 'This maintenance task has no device-model context yet, so Device Expert is unavailable.'}
                    </div>
                  </div>
                  <div style={layoutStyles.expertActions}>
                    <button
                      type="button"
                      style={{
                        ...layoutStyles.buttonPrimary,
                        opacity:
                          deviceExpertLoading ||
                          !deviceExpertModelId ||
                          (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                            ? 0.65
                            : 1,
                        cursor:
                          deviceExpertLoading ||
                          !deviceExpertModelId ||
                          (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                            ? 'not-allowed'
                            : 'pointer'
                      }}
                      onClick={askDeviceExpert}
                      disabled={
                        deviceExpertLoading ||
                        !deviceExpertModelId ||
                        (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                      }
                    >
                      {deviceExpertLoading ? 'Asking...' : 'Ask Device Expert'}
                    </button>
                    <button
                      type="button"
                      style={{
                        ...layoutStyles.buttonSecondary,
                        opacity: deviceExpertLoading ? 0.65 : 1,
                        cursor: deviceExpertLoading ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => {
                        setDeviceExpertQuestion('');
                        setDeviceExpertMessages([]);
                        setDeviceExpertError(null);
                        setDeviceExpertConversationId(null);
                      }}
                      disabled={deviceExpertLoading}
                    >
                      Clear
                    </button>
                  </div>
                  {deviceExpertError && <div style={layoutStyles.expertError}>{deviceExpertError}</div>}
                  {deviceExpertMessages.length > 0 && (
                    <div style={layoutStyles.expertMessages}>
                      {deviceExpertMessages.map(message => (
                        <div
                          key={message.id}
                          style={{
                            ...layoutStyles.expertMessage,
                            ...(message.role === 'assistant'
                              ? layoutStyles.expertAssistant
                              : layoutStyles.expertUser)
                          }}
                        >
                          <strong>{message.role === 'assistant' ? 'Assistant' : 'You'}:</strong>{' '}
                          {message.role === 'assistant'
                            ? formatDeviceExpertText(message.content)
                            : message.content}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div style={checklistStyles.section}>
            <div style={checklistStyles.sectionHeader}>
              <div>
                <div style={layoutStyles.fieldLabel}>Maintenance checklist</div>
                <div style={checklistStyles.sectionSub}>
                  {referenceLoading
                    ? 'Loading reference...'
                    : referenceMeta
                    ? `Reference ${referenceMeta.maintenanceInterval ?? 'Monthly'}`
                    : 'Reference unavailable'}
                </div>
              </div>
            </div>
            {referenceLoading ? (
              <div style={checklistStyles.loading}>Loading maintenance checklist...</div>
            ) : checkupSteps.length === 0 ? (
              <div style={checklistStyles.empty}>No maintenance steps found for this device model.</div>
            ) : (
              checkupSteps.map((step, index) => {
                const totalChecklist = step.checklist.length;
                const stepComplete =
                  (totalChecklist > 0 && step.checklist.every(item => item.completed)) || step.notApplicable;
                const completedStyle = stepComplete
                  ? { borderColor: BRAND_SUCCESS_BORDER, background: BRAND_SUCCESS_SOFT }
                  : {};
                const draftPart = partDrafts[step.id] || createEmptyPartDraft();
                const partScanState = partScanStates[step.id] || 'idle';
                const partScanMessage = partScanMessages[step.id] || null;
                const canAddPart = Boolean(
                  draftPart.partNumber.trim() || draftPart.description.trim()
                );
                return (
                  <div key={step.id} style={{ ...checklistStyles.stepCard, ...completedStyle }}>
                    <div style={checklistStyles.stepHeader}>
                      <div>
                        <div style={checklistStyles.stepTitle}>
                          <span style={checklistStyles.stepIndex}>{index + 1}.</span>
                          {step.label}
                        </div>
                        {step.summary && <div style={checklistStyles.stepSummary}>{step.summary}</div>}
                      </div>
                      <div style={checklistStyles.stepActions}>
                        <button
                          type="button"
                          style={checklistStyles.flagButton}
                          onClick={() => handleFlagStep(step.label)}
                        >
                          Flag documentation issue
                        </button>
                        <button
                          type="button"
                          style={checklistStyles.toggleButton}
                          onClick={() => toggleStepCollapsed(step.id)}
                          disabled={workspaceReadOnly}
                        >
                          {step.collapsed ? 'Show' : 'Hide'}
                        </button>
                      </div>
                    </div>
                    {!step.collapsed && (
                      <>
                        <div style={checklistStyles.checklist}>
                          {step.checklist.length === 0 ? (
                            <div style={checklistStyles.empty}>No checklist entries for this step.</div>
                          ) : (
                            step.checklist.map(item => {
                              const linkedText = renderReferenceImageTextLinks(step, item);
                              return (
                                <div key={item.id} style={checklistStyles.checklistItem}>
                                  <label style={checklistStyles.checklistToggle}>
                                    <input
                                      type="checkbox"
                                      checked={item.completed}
                                      onChange={() => toggleChecklistItem(step.id, item.id)}
                                      disabled={workspaceReadOnly}
                                    />
                                    <div style={checklistStyles.checklistLabel}>
                                      {linkedText.content}
                                    </div>
                                  </label>
                                  {renderMeasurementCapture(step.id, item)}
                                </div>
                              );
                            })
                          )}
                        </div>
                        <div style={checklistStyles.partsCard}>
                          <div style={checklistStyles.partsTitle}>Parts replaced</div>
                          {referenceKitPart && (
                            <div style={checklistStyles.referenceKitChipRow}>
                              <span style={checklistStyles.referenceKitChipLabel}>Reference kit</span>
                              <button
                                type="button"
                                style={{
                                  ...checklistStyles.referenceKitChip,
                                  opacity: workspaceReadOnly ? 0.55 : 1,
                                  cursor: workspaceReadOnly ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleSelectReferenceKitPart(step.id)}
                                disabled={workspaceReadOnly}
                                title={referenceKitPart.description || referenceKitPart.partNumber}
                              >
                                {referenceKitPart.partNumber}
                              </button>
                            </div>
                          )}
                          {step.partsUsed.length === 0 ? (
                            <div style={checklistStyles.empty}>No parts recorded.</div>
                          ) : (
                            <div style={checklistStyles.partsTable}>
                              <div style={{ ...checklistStyles.partsRow, ...checklistStyles.partsHeaderRow }}>
                                <span>Part number</span>
                                <span>Description</span>
                                <span>Batch / lot</span>
                                <span>Qty</span>
                                <span />
                              </div>
                              {step.partsUsed.map(part => (
                                <div key={part.id} style={checklistStyles.partsRow}>
                                  <span style={checklistStyles.partCellValue}>{part.partNumber || '-'}</span>
                                  <span style={checklistStyles.partCellValue}>{part.description || '-'}</span>
                                  <span style={checklistStyles.partCellValue}>{part.batchNumber || '-'}</span>
                                  <span style={checklistStyles.partCellValue}>
                                    {part.quantity === null ? '-' : String(part.quantity)}
                                  </span>
                                  <div style={checklistStyles.partActions}>
                                    <button
                                      type="button"
                                      style={{
                                        ...checklistStyles.partRemoveButton,
                                        ...(workspaceReadOnly ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                                      }}
                                      onClick={() => handleRemovePart(step.id, part.id)}
                                      disabled={workspaceReadOnly}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={checklistStyles.partsTable}>
                            <div style={{ ...checklistStyles.partsRow, ...checklistStyles.partsHeaderRow }}>
                              <span>Add new part</span>
                              <span />
                              <span />
                              <span />
                              <span />
                            </div>
                            <div style={checklistStyles.partsRow}>
                              <input
                                type="text"
                                style={checklistStyles.partCellInput}
                                placeholder="Part number"
                                value={draftPart.partNumber}
                                onChange={event =>
                                  handlePartDraftFieldChange(step.id, 'partNumber', event.target.value)
                                }
                                disabled={workspaceReadOnly}
                              />
                              <input
                                type="text"
                                style={checklistStyles.partCellInput}
                                placeholder="Part description"
                                value={draftPart.description}
                                onChange={event =>
                                  handlePartDraftFieldChange(step.id, 'description', event.target.value)
                                }
                                disabled={workspaceReadOnly}
                              />
                              <input
                                type="text"
                                style={checklistStyles.partCellInput}
                                placeholder="Batch or lot number"
                                value={draftPart.batchNumber}
                                onChange={event =>
                                  handlePartDraftFieldChange(step.id, 'batchNumber', event.target.value)
                                }
                                disabled={workspaceReadOnly}
                              />
                              <input
                                type="text"
                                inputMode="decimal"
                                style={checklistStyles.partCellInput}
                                placeholder="Qty"
                                value={draftPart.quantity}
                                onChange={event => handlePartDraftQuantityChange(step.id, event.target.value)}
                                disabled={workspaceReadOnly}
                              />
                              <div style={checklistStyles.partActions}>
                                <button
                                  type="button"
                                  style={{
                                    ...checklistStyles.partAddButton,
                                    opacity: !canAddPart || workspaceReadOnly ? 0.5 : 1,
                                    cursor: !canAddPart || workspaceReadOnly ? 'not-allowed' : 'pointer'
                                  }}
                                  onClick={() => handleAddPart(step.id)}
                                  disabled={!canAddPart || workspaceReadOnly}
                                >
                                  Add part
                                </button>
                                <button
                                  type="button"
                                  style={{
                                    ...checklistStyles.partScanButton,
                                    opacity: partScanState === 'pending' || workspaceReadOnly ? 0.5 : 1,
                                    cursor:
                                      partScanState === 'pending' || workspaceReadOnly
                                        ? 'not-allowed'
                                        : 'pointer'
                                  }}
                                  onClick={() => handlePartScanClick(step.id)}
                                  disabled={partScanState === 'pending' || workspaceReadOnly}
                                >
                                  {partScanState === 'pending' ? 'Scanning...' : 'Scan label'}
                                </button>
                              </div>
                            </div>
                          </div>
                          {partScanMessage && (
                            <div
                              style={{
                                ...checklistStyles.scanMessage,
                                ...(partScanState === 'error'
                                  ? checklistStyles.scanMessageError
                                  : partScanState === 'success'
                                  ? checklistStyles.scanMessageSuccess
                                  : {})
                              }}
                            >
                              {partScanMessage}
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={el => {
                              partScanInputRefs.current[step.id] = el;
                            }}
                            onChange={event => handlePartScanInputChange(step.id, event)}
                            disabled={workspaceReadOnly}
                          />
                        </div>
                        <div style={checklistStyles.notesCard}>
                          <div style={checklistStyles.notesLabelRow}>
                            <div style={layoutStyles.fieldLabel}>Photos / inspection notes</div>
                            <div style={checklistStyles.attachmentToolbar}>
                              <button
                                type="button"
                                style={{
                                  ...checklistStyles.addAttachmentButton,
                                  opacity:
                                    step.attachments.length >= MAX_STEP_ATTACHMENTS || workspaceReadOnly
                                      ? 0.5
                                      : 1,
                                  cursor:
                                    step.attachments.length >= MAX_STEP_ATTACHMENTS || workspaceReadOnly
                                      ? 'not-allowed'
                                      : 'pointer'
                                }}
                                onClick={() => fileInputRefs.current[step.id]?.click()}
                                disabled={step.attachments.length >= MAX_STEP_ATTACHMENTS || workspaceReadOnly}
                              >
                                Add photo / file
                              </button>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                <input
                                  type="checkbox"
                                  checked={step.notApplicable}
                                  onChange={event => handleToggleNotApplicable(step.id, event.target.checked)}
                                  disabled={workspaceReadOnly}
                                />
                                <span style={{ fontSize: '0.85rem', color: BRAND_MUTED }}>Not applicable</span>
                              </label>
                            </div>
                          </div>
                          <textarea
                            style={checklistStyles.notesTextarea}
                            placeholder="Add observations, risks, or follow-up tasks..."
                            value={step.notes}
                            onChange={event => handleStepNoteChange(step.id, event.target.value)}
                            disabled={workspaceReadOnly}
                          />
                          {step.attachments.length === 0 ? (
                            <div style={{ fontSize: '0.85rem', color: BRAND_MUTED }}>
                              No supporting files attached yet.
                            </div>
                          ) : (
                            <div style={checklistStyles.attachmentList}>
                              {step.attachments.map(attachment => (
                                <span key={attachment.id} style={checklistStyles.attachmentPill}>
                                  {attachment.name}
                                  <button
                                    type="button"
                                    style={checklistStyles.removeAttachmentButton}
                                    onClick={() => handleRemoveAttachment(step.id, attachment.id)}
                                    disabled={workspaceReadOnly}
                                  >
                                    x
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={el => {
                              fileInputRefs.current[step.id] = el;
                            }}
                            onChange={event => handleAttachmentInputChange(step.id, event)}
                            disabled={workspaceReadOnly}
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {previewImage && (
        <div style={checklistStyles.previewOverlay} onClick={closePreviewImages}>
          <div style={checklistStyles.previewModal} onClick={event => event.stopPropagation()}>
            <div style={checklistStyles.previewHeader}>
              <div style={checklistStyles.previewTitle}>
                <strong>{previewImage.label || 'Reference image'}</strong>
                <span style={checklistStyles.previewCounter}>
                  {previewImageCount > 0 ? `${previewImageIndex + 1} / ${previewImageCount}` : null}
                </span>
              </div>
              <button type="button" style={checklistStyles.previewCloseButton} onClick={closePreviewImages}>
                Close
              </button>
            </div>
            <div style={checklistStyles.previewControls}>
              {previewImageCount > 1 && (
                <>
                  <button
                    type="button"
                    style={checklistStyles.previewControlButton}
                    onClick={showPreviousPreviewImage}
                    disabled={previewImageIndex <= 0}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    style={checklistStyles.previewControlButton}
                    onClick={showNextPreviewImage}
                    disabled={previewImageIndex >= previewImageCount - 1}
                  >
                    Next
                  </button>
                </>
              )}
              <button
                type="button"
                style={checklistStyles.previewControlButton}
                onClick={() => setPreviewZoom(value => Math.max(100, value - 25))}
                aria-label="Zoom out"
              >
                -
              </button>
              <input
                type="range"
                min={100}
                max={300}
                step={25}
                value={previewZoom}
                onChange={event => setPreviewZoom(Number(event.target.value))}
                style={checklistStyles.previewZoomSlider}
                aria-label="Image zoom"
              />
              <button
                type="button"
                style={checklistStyles.previewControlButton}
                onClick={() => setPreviewZoom(value => Math.min(300, value + 25))}
                aria-label="Zoom in"
              >
                +
              </button>
              <button
                type="button"
                style={checklistStyles.previewControlButton}
                onClick={() => setPreviewZoom(100)}
              >
                100%
              </button>
              <span style={checklistStyles.previewZoomValue}>{previewZoom}%</span>
            </div>
            {previewImageCount > 1 && (
              <div style={checklistStyles.previewGalleryStrip}>
                {previewImages.map((image, index) => (
                  <button
                    type="button"
                    key={image.id || `${index}`}
                    style={{
                      ...checklistStyles.previewGalleryThumbButton,
                      ...(index === previewImageIndex
                        ? checklistStyles.previewGalleryThumbButtonActive
                        : {})
                    }}
                    onClick={() => selectPreviewImage(index)}
                    aria-label={`Open reference image ${index + 1}`}
                  >
                    <AuthenticatedReferenceImage
                      src={image.fileUrl}
                      alt={image.label || 'reference image'}
                      style={checklistStyles.previewGalleryThumb}
                      placeholderStyle={checklistStyles.previewGalleryThumbPlaceholder}
                    />
                  </button>
                ))}
              </div>
            )}
            <div style={checklistStyles.previewImageStage}>
              <div
                style={{
                  ...checklistStyles.previewImageCanvas,
                  width: `${previewZoom}%`,
                  height: `${previewZoom}%`,
                  alignItems: previewZoom > 100 ? 'flex-start' : 'center',
                  justifyContent: previewZoom > 100 ? 'flex-start' : 'center'
                }}
              >
                <AuthenticatedReferenceImage
                  src={previewImage.fileUrl}
                  alt="Reference image"
                  style={checklistStyles.previewImage}
                  placeholderStyle={checklistStyles.previewPlaceholder}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <RepairServiceReportModal
        open={serviceReportModalOpen}
        document={serviceReportDraft}
        saving={serviceReportSaving}
        regenerating={serviceReportLoading}
        sending={serviceReportSending}
        error={serviceReportError}
        mode={serviceReportMode}
        subtitle={
          serviceReportMode === 'view'
            ? 'Locked saved maintenance report snapshot'
            : 'Preview the maintenance report generated from the primary service report template.'
        }
        hint={
          serviceReportMode === 'view'
            ? savedServiceReportSent
              ? 'This service report has already been sent to the customer.'
              : 'This saved service report has not been sent yet. Regenerate it after summary or template changes, then save the refreshed report before sending.'
            : 'Review the template-filled report, then create the locked service report snapshot.'
        }
        canRegenerate={serviceReportMode === 'view' && canRebuildServiceReport}
        regenerateLabel="Regenerate service report"
        onRegenerate={handleCreateServiceReport}
        saveLabel={workspaceData?.serviceReport?.document ? 'Save refreshed report' : 'Create report'}
        canSend={
          serviceReportMode === 'view' &&
          !workspaceReadOnly &&
          taskStatus === 'COMPLETED' &&
          Boolean(workspaceData?.serviceReport?.id) &&
          !savedServiceReportSent
        }
        sendLabel="Send service report"
        onSend={handleSendServiceReport}
        onClose={closeServiceReportModal}
        onSave={handleSaveServiceReport}
      />

      {statusDialog && (
        <div style={dialogStyles.overlay}>
          <div style={dialogStyles.card}>
            <div style={dialogStyles.title}>
              {statusDialog.mode === 'COMPLETED'
                ? 'Complete maintenance'
                : statusDialog.mode === 'ON_HOLD'
                ? 'Place maintenance on hold'
                : 'Update maintenance status'}
            </div>
            <div style={dialogStyles.subtitle}>
              {statusDialog.mode === 'COMPLETED'
                ? 'Explain why the maintenance is complete even though some checklist items remain.'
                : statusDialog.mode === 'ON_HOLD'
                ? 'Leave a quick note so the next technician knows why work is paused.'
                : 'Add an optional note while re-opening this maintenance task.'}
            </div>
            {(statusDialog.requireReason || statusDialog.mode !== 'IN_PROGRESS') && (
              <textarea
                style={dialogStyles.textarea}
                value={statusDialog.reason}
                onChange={event => handleStatusDialogChange(event.target.value)}
                placeholder={
                  statusDialog.requireReason ? 'Reason is required...' : 'Add an optional note...'
                }
              />
            )}
            {statusDialogError && <div style={dialogStyles.error}>{statusDialogError}</div>}
            <div style={dialogStyles.actions}>
              <button
                type="button"
                style={{ ...layoutStyles.buttonSecondary, padding: '0.4rem 1.2rem' }}
                onClick={closeStatusDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...layoutStyles.buttonPrimary, padding: '0.4rem 1.2rem' }}
                onClick={handleConfirmStatusDialog}
                disabled={workspaceReadOnly}
              >
                Apply status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}





